import React, { useCallback, useEffect, useMemo, useState } from 'react';
import client from '../../api/client';
import { getTrainingEntryTypes } from '../../api/training';
import { useAuth } from '../../contexts/AuthContext';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import DateInputField from '../../components/DateInputField';
import {
  downloadDienstzeitProtokollPdf,
} from '../../components/pdf/dienstzeitPdfExport';
import {
  createFeedbackEntry,
  getFeedbackCapabilities,
  getFeedbackOptions,
  uploadFeedbackAssets,
} from '../../api/feedback';
import {
  buildIncidentSubmitPayload,
  getInitialIncidentFormState,
  IncidentFeedbackFormFields,
  isIncidentFormValid,
} from '../rueckmeldung/incidentFormShared';
import {
  getInitialWizardAnswers,
  getResponsibleQuestion,
  getSelectedDate,
  getSelectedEntryType,
  getTimeQuestion,
  getTodayIsoDate,
  hasEquipmentCheckDayEntryAccess,
  hasSrhtBasicCourseAccess,
  isExclusiveApplicantUser,
  sortResponsibleUsers,
  logbookEntryWizardSchema,
} from '../../config/logbookEntryWizardSchema';
import {
  calculateRawServiceHours,
  calculateEffectiveServiceHoursForWizard,
  getLogbookEntryTypeRule,
  getNoBreakCompensationPauseHours,
  getPauseHoursForDuration,
} from '../../config/logbookEntryRules';
import { isQuestionFiveSequenceComplete } from './questionFlow';
import {
  filterResponsibleCandidates,
  getResponsibleCandidateLabel,
} from './responsibleSearch';
import {
  buildQuestionFiveHoursValue,
  getQuestionFiveSuggestedHours,
  normalizeQuestionFiveHourPart,
  normalizeQuestionFiveMinutePart,
  shouldShowQuestionFiveSyncNotice,
} from './questionFiveHours';
import {
  getIncidentQuestionNumber,
  getPdfQuestionNumber,
  shouldShowIncidentQuestion,
} from './incidentQuestionFlow';
import {
  canDeselectOwnLogbook,
  initializeLogbookTargetSelection,
  toggleLogbookTargetSelection,
} from './logbookTargetSelection';

const ZERO_DURATION = '00:00';
const A3_DEFAULT_TIME_FROM = '07:00';
const A3_DEFAULT_TIME_TO = '15:42';
const A3_PAUSE_DEFAULT = 'ja';
const FULL_DAY_TOTAL_MINUTES = 522;
const FULL_DAY_SUGGESTION_MIN_SERVICE_HOURS = 7 + (40 / 60);
const FULL_DAY_SUGGESTION_MAX_SERVICE_HOURS = 8.2;
const EQUIPMENT_CHECK_DAY_ENTRY_TYPE = 'Geräteprüfung (Tagesdienst)';
const EXTERNAL_TRAINING_ENTRY_TYPE = 'Externe Fortbildung';
const HONORAR_ENTRY_TYPE = 'Honorartätigkeit';
const SRHT_BASIC_COURSE_ENTRY_TYPE = 'Grundausbildungslehrgang SRHT';
const RESPONSIBLE_MODE_INTERNAL = 'internal';
const RESPONSIBLE_MODE_EXTERNAL = 'external';
const DAY_SERVICE_SCALE_FACTOR = 1.17;
const ENTRY_VARIANT_TE1 = 'te1te2';
const ENTRY_VARIANT_TE1_FLEX = 'te1te2-flex';
const ENTRY_VARIANT_HONORAR = 'honorar';
const ENTRY_VARIANT_HONORAR_FLEX = 'honorar-flex';
const KEYWORD_MAX_PER_ENTRY = 10;
function formatHours(value) {
  const rounded = Math.round(Number(value) * 100) / 100;
  if (Number.isNaN(rounded)) {
    return '0';
  }

  return rounded
    .toFixed(2)
    .replace(/\.00$/, '')
    .replace(/(\.\d)0$/, '$1');
}

function minutesToDurationString(totalMinutes) {
  const numeric = Number(totalMinutes);
  if (!Number.isFinite(numeric) || numeric < 0) {
    return '';
  }

  const roundedMinutes = Math.round(numeric);
  const hours = Math.floor(roundedMinutes / 60);
  const minutes = roundedMinutes % 60;
  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
}

function decimalHoursToDurationString(hoursValue) {
  const numeric = Number(hoursValue);
  if (!Number.isFinite(numeric) || numeric < 0) {
    return '';
  }

  return minutesToDurationString(numeric * 60);
}

function formatResponsibleDisplayName(name, company = '') {
  const normalizedName = String(name || '').trim();
  const normalizedCompany = String(company || '').trim();
  if (!normalizedName) {
    return '';
  }

  return normalizedCompany ? `${normalizedName} (${normalizedCompany})` : normalizedName;
}

function durationStringToDecimalHours(value) {
  const normalized = String(value || '').trim();
  if (!normalized) {
    return null;
  }

  const match = normalized.match(/^(\d{1,2}):(\d{2})$/);
  if (!match) {
    return null;
  }

  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  if (!Number.isFinite(hours) || !Number.isFinite(minutes) || minutes < 0 || minutes >= 60) {
    return null;
  }

  return Number(((hours * 60 + minutes) / 60).toFixed(2));
}

function normalizeDurationInput(value) {
  const raw = String(value || '').trim();
  if (!raw) {
    return '';
  }

  const fromDuration = durationStringToDecimalHours(raw);
  if (fromDuration !== null) {
    return decimalHoursToDurationString(fromDuration);
  }

  const numeric = Number(raw.replace(',', '.'));
  if (Number.isFinite(numeric) && numeric >= 0) {
    return decimalHoursToDurationString(numeric);
  }

  return '';
}

function normalizeKeywordToken(value) {
  const normalized = String(value || '').trim().replace(/\s+/g, ' ');
  if (!normalized) {
    return '';
  }

  return normalized.slice(0, 160);
}

function normalizeKeywordList(values = []) {
  if (!Array.isArray(values)) {
    return [];
  }

  const seen = new Set();
  const result = [];

  values.forEach((value) => {
    const token = normalizeKeywordToken(value);
    if (!token) {
      return;
    }

    const normalized = token.toLocaleLowerCase('de');
    if (seen.has(normalized)) {
      return;
    }

    seen.add(normalized);
    result.push(token);
  });

  return result.slice(0, KEYWORD_MAX_PER_ENTRY);
}

function getDurationParts(value) {
  const normalized = normalizeDurationInput(value) || ZERO_DURATION;
  const [hoursPart, minutesPart] = normalized.split(':');
  return {
    hours: String(hoursPart || '00').padStart(2, '0'),
    minutes: String(minutesPart || '00').padStart(2, '0'),
  };
}

function cycleWheelOption(currentValue, optionValues, deltaY) {
  if (!Array.isArray(optionValues) || optionValues.length === 0 || deltaY === 0) {
    return currentValue;
  }

  const currentIndex = optionValues.indexOf(currentValue);
  const direction = deltaY > 0 ? 1 : -1;

  if (currentIndex === -1) {
    return direction > 0 ? optionValues[0] : optionValues[optionValues.length - 1];
  }

  const nextIndex = Math.max(0, Math.min(optionValues.length - 1, currentIndex + direction));
  return optionValues[nextIndex];
}

function normalizeTimeValue(rawValue) {
  if (!rawValue || !rawValue.includes(':')) {
    return '';
  }

  const [hoursPart, minutesPart] = String(rawValue).split(':').map(Number);
  if (Number.isNaN(hoursPart) || Number.isNaN(minutesPart)) {
    return '';
  }

  const normalizedMinutes = Math.max(0, Math.min(59, minutesPart));
  const finalHours = ((hoursPart % 24) + 24) % 24;
  return `${String(finalHours).padStart(2, '0')}:${String(normalizedMinutes).padStart(2, '0')}`;
}

function shiftTimeByMinute(currentValue, direction) {
  if (!currentValue || !currentValue.includes(':')) {
    return '';
  }

  const [hoursPart, minutesPart] = currentValue.split(':').map(Number);
  if (Number.isNaN(hoursPart) || Number.isNaN(minutesPart)) {
    return currentValue;
  }

  const totalMinutes = (hoursPart * 60) + minutesPart;
  const shifted = totalMinutes + direction;
  const normalized = ((shifted % (24 * 60)) + (24 * 60)) % (24 * 60);

  const nextHours = String(Math.floor(normalized / 60)).padStart(2, '0');
  const nextMinutes = String(normalized % 60).padStart(2, '0');
  return `${nextHours}:${nextMinutes}`;
}

function addMinutesToTime(timeValue, minutesToAdd) {
  if (!timeValue || !timeValue.includes(':')) {
    return '';
  }

  const [hoursPart, minutesPart] = timeValue.split(':').map(Number);
  if (Number.isNaN(hoursPart) || Number.isNaN(minutesPart)) {
    return '';
  }

  const totalMinutes = (hoursPart * 60) + minutesPart + minutesToAdd;
  const normalizedMinutes = ((totalMinutes % (24 * 60)) + (24 * 60)) % (24 * 60);
  const nextHours = String(Math.floor(normalizedMinutes / 60)).padStart(2, '0');
  const nextMinutes = String(normalizedMinutes % 60).padStart(2, '0');
  return `${nextHours}:${nextMinutes}`;
}

function isDozentDayEntryType(entryType, entryTypeMeta = null) {
  return getLogbookEntryTypeRule(entryType, entryTypeMeta).row4Mode === 'te1-flex-honorar';
}

function isSrhtBasicCourseEntryType(entryType) {
  return entryType === SRHT_BASIC_COURSE_ENTRY_TYPE;
}

function getDefaultDayEntryTimes() {
  return {
    timeFrom: A3_DEFAULT_TIME_FROM,
    timeTo: A3_DEFAULT_TIME_TO,
  };
}

function getDayEntryBaseVariant(entryVariant) {
  if ([ENTRY_VARIANT_HONORAR, ENTRY_VARIANT_HONORAR_FLEX].includes(entryVariant)) {
    return ENTRY_VARIANT_HONORAR;
  }

  if ([ENTRY_VARIANT_TE1, ENTRY_VARIANT_TE1_FLEX].includes(entryVariant)) {
    return ENTRY_VARIANT_TE1;
  }

  return '';
}

function isDayEntryFlexEnabled(entryVariant) {
  return entryVariant === ENTRY_VARIANT_TE1_FLEX || entryVariant === ENTRY_VARIANT_HONORAR_FLEX;
}

function buildDayEntryVariant(baseVariant, flexEnabled) {
  if (!baseVariant) {
    return '';
  }

  if (baseVariant === ENTRY_VARIANT_HONORAR) {
    return flexEnabled ? ENTRY_VARIANT_HONORAR_FLEX : ENTRY_VARIANT_HONORAR;
  }

  return flexEnabled ? ENTRY_VARIANT_TE1_FLEX : ENTRY_VARIANT_TE1;
}

function getDayServiceCodeGroup(entryVariant) {
  if ([ENTRY_VARIANT_TE1, ENTRY_VARIANT_TE1_FLEX].includes(entryVariant)) {
    return 'T';
  }

  if ([ENTRY_VARIANT_HONORAR, ENTRY_VARIANT_HONORAR_FLEX].includes(entryVariant)) {
    return 'HONORAR';
  }

  if (entryVariant === 'a3' || entryVariant === 'za3') {
    return 'A';
  }

  return '';
}

function isExactA3ServiceDuration(serviceDuration) {
  const numeric = Number(serviceDuration);
  if (!Number.isFinite(numeric)) {
    return false;
  }

  return Math.abs(numeric - FULL_DAY_SUGGESTION_MAX_SERVICE_HOURS) <= 0.01;
}

function resolveDayServiceVariantForGroup(group, serviceDuration) {
  if (group === 'T') {
    return ENTRY_VARIANT_TE1;
  }

  if (group === 'HONORAR') {
    return ENTRY_VARIANT_HONORAR;
  }

  if (group === 'A') {
    return isExactA3ServiceDuration(serviceDuration) ? 'a3' : 'za3';
  }

  return '';
}

function getDisplayedTimeQuestion(entryType, entryVariant, fallbackQuestion, entryTypeMeta = null) {
  if (entryVariant === ENTRY_VARIANT_TE1_FLEX || entryVariant === ENTRY_VARIANT_TE1) {
    return '4. Welcher Zeitraum der tatsächlichen Arbeitsanwesenheit soll erfasst werden?';
  }

  if (!isDozentDayEntryType(entryType, entryTypeMeta)) {
    return fallbackQuestion;
  }

  const baseVariant = getDayEntryBaseVariant(entryVariant);
  if (baseVariant === ENTRY_VARIANT_TE1) {
    return '4. Welcher Zeitraum soll für die T-Eintragung (automatisch T1/T2) verwendet werden?';
  }

  if (baseVariant === ENTRY_VARIANT_HONORAR) {
    return '4. Welcher Zeitraum soll für die Honorartätigkeit verwendet werden?';
  }

  return '4. Wähle zuerst T oder Honorartätigkeit und aktiviere bei Bedarf einen flexiblen Zeitraum.';
}

function isCustomPeriodVariant(entryType, entryVariant) {
  if (entryVariant === ENTRY_VARIANT_TE1_FLEX || entryVariant === ENTRY_VARIANT_HONORAR_FLEX) {
    return true;
  }

  if (isDozentDayEntryType(entryType)) {
    return isDayEntryFlexEnabled(entryVariant);
  }

  return entryVariant === 'za3' || entryVariant === 'custom24h' || entryVariant === 'customOnly';
}

function getConfiguredEntryTypeMeta(activityTypeMetaByValue, entryType) {
  if (!(activityTypeMetaByValue instanceof Map)) {
    return null;
  }

  return activityTypeMetaByValue.get(entryType) || null;
}

function usesPauseQuestion(entryType, entryVariant, activityTypeMetaByValue = null) {
  if (entryVariant === ENTRY_VARIANT_TE1 || entryVariant === ENTRY_VARIANT_TE1_FLEX) {
    return true;
  }

  const effectiveEntryType = getEffectiveEntryTypeForVariant(entryType, entryVariant);
  return getLogbookEntryTypeRule(
    effectiveEntryType,
    getConfiguredEntryTypeMeta(activityTypeMetaByValue, effectiveEntryType),
  ).showPauseQuestion;
}

function hasHoursValue(value) {
  if (value === '' || value === null || typeof value === 'undefined') {
    return false;
  }

  const numeric = durationStringToDecimalHours(normalizeDurationInput(value));
  return Number.isFinite(numeric) && numeric >= 0;
}

function getFixedPauseHoursForVariant(entryType, entryVariant) {
  if (entryVariant === 'a3') {
    return 0.5;
  }

  return 0;
}

function calculateDisplayedServiceHours(
  entryType,
  entryVariant,
  timeFrom,
  timeTo,
  pauseAnswer,
  enforcedNoBreakPauseHours = 0,
  activityTypeMetaByValue = null,
) {
  const rawServiceHours = calculateRawServiceHours(timeFrom, timeTo);
  if (rawServiceHours === null) {
    return null;
  }

  const fixedPauseHours = getFixedPauseHoursForVariant(entryType, entryVariant);
  if (fixedPauseHours > 0) {
    return Math.max(0, Number((rawServiceHours - fixedPauseHours).toFixed(2)));
  }

  if (!usesPauseQuestion(entryType, entryVariant, activityTypeMetaByValue)) {
    return rawServiceHours;
  }

  return calculateEffectiveServiceHoursForWizard(
    entryType,
    timeFrom,
    timeTo,
    pauseAnswer,
    enforcedNoBreakPauseHours,
  );
}

function getEffectiveEntryTypeForVariant(entryType, entryVariant) {
  if (isDozentDayEntryType(entryType) && getDayEntryBaseVariant(entryVariant) === ENTRY_VARIANT_HONORAR) {
    return HONORAR_ENTRY_TYPE;
  }

  return entryType;
}

function getEntryVariantLabel(entryType, entryVariant) {
  if (isDozentDayEntryType(entryType)) {
    const baseVariant = getDayEntryBaseVariant(entryVariant);
    const flexEnabled = isDayEntryFlexEnabled(entryVariant);

    if (baseVariant === ENTRY_VARIANT_TE1) {
      return flexEnabled ? 'T (automatisch T1/T2), Flexibler Zeitraum' : 'T (automatisch T1/T2)';
    }

    if (baseVariant === ENTRY_VARIANT_HONORAR) {
      return flexEnabled ? 'Honorar, Flexibler Zeitraum' : 'Honorar';
    }
  }

  return '';
}

function getServiceDurationLabelForVariant(entryType, entryVariant, activityTypeMetaByValue = null) {
  const effectiveEntryType = getEffectiveEntryTypeForVariant(entryType, entryVariant);
  return getLogbookEntryTypeRule(
    effectiveEntryType,
    getConfiguredEntryTypeMeta(activityTypeMetaByValue, effectiveEntryType),
  ).serviceDurationLabel;
}

function getValidPauseAnswer(entryType, entryVariant, pauseAnswer, activityTypeMetaByValue = null) {
  if (!usesPauseQuestion(entryType, entryVariant, activityTypeMetaByValue)) {
    return '';
  }

  if (entryVariant === 'a3') {
    return A3_PAUSE_DEFAULT;
  }

  if (!isCustomPeriodVariant(entryType, entryVariant)) {
    return '';
  }

  return pauseAnswer || '';
}

function isDayServiceEntryType(entryType, entryTypeMeta = null) {
  const row4Mode = getLogbookEntryTypeRule(entryType, entryTypeMeta).row4Mode;
  return row4Mode === 'a3-za3' || row4Mode === 'te1-flex-honorar';
}

function isConfiguredDayServiceEntryType(entryType, activityTypeMetaByValue = null) {
  const configuredMeta = getConfiguredEntryTypeMeta(activityTypeMetaByValue, entryType);
  return isDayServiceEntryType(entryType, configuredMeta);
}

function scaleWorkHours(entryType, unscaledHours, activityTypeMetaByValue = null) {
  const numeric = Number(unscaledHours);
  if (!Number.isFinite(numeric)) {
    return null;
  }

  const configuredMeta = getConfiguredEntryTypeMeta(activityTypeMetaByValue, entryType);
  const shouldScale = typeof configuredMeta?.scaleWorkHours === 'boolean'
    ? configuredMeta.scaleWorkHours
    : isDayServiceEntryType(entryType, configuredMeta);

  if (!shouldScale) {
    return numeric;
  }

  return Number((numeric * DAY_SERVICE_SCALE_FACTOR).toFixed(2));
}

function getPauseHoursForExport(
  entryType,
  entryVariant,
  timeFrom,
  timeTo,
  pauseAnswer,
  enforcedNoBreakPauseHours,
  activityTypeMetaByValue = null,
) {
  const rawServiceHours = calculateRawServiceHours(timeFrom, timeTo);
  if (rawServiceHours === null) {
    return 0;
  }

  const fixedPauseHours = getFixedPauseHoursForVariant(entryType, entryVariant);
  if (fixedPauseHours > 0) {
    return fixedPauseHours;
  }

  if (!usesPauseQuestion(entryType, entryVariant, activityTypeMetaByValue)) {
    return 0;
  }

  if (pauseAnswer === 'ja') {
    return getPauseHoursForDuration(rawServiceHours, 'ja');
  }

  const explicitNoBreakPause = Number(enforcedNoBreakPauseHours);
  if (Number.isFinite(explicitNoBreakPause) && explicitNoBreakPause > 0) {
    return explicitNoBreakPause;
  }

  return getNoBreakCompensationPauseHours(rawServiceHours);
}

function EintragVornehmenPage() {
  const { user } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [answers, setAnswers] = useState(getInitialWizardAnswers());
  const [, setShowForm] = useState(false);
  const [confirmSaveOpen, setConfirmSaveOpen] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [saveWarning, setSaveWarning] = useState('');
  const [saveSuccess, setSaveSuccess] = useState('');
  const [saveInjuryWarning, setSaveInjuryWarning] = useState(false);
  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [usersError, setUsersError] = useState('');
  const [configuredEntryTypeOptions, setConfiguredEntryTypeOptions] = useState([]);
  const [configuredEntryTypesReady, setConfiguredEntryTypesReady] = useState(false);
  const [incidentAnswer, setIncidentAnswer] = useState('');
  const [incidentFormState, setIncidentFormState] = useState(getInitialIncidentFormState());
  const [incidentAttachmentFiles, setIncidentAttachmentFiles] = useState([]);
  const [incidentOptions, setIncidentOptions] = useState({ entryTypes: [], responsibles: [] });
  const [incidentOptionsLoading, setIncidentOptionsLoading] = useState(false);
  const [incidentOptionsError, setIncidentOptionsError] = useState('');
  const [incidentCanCreate, setIncidentCanCreate] = useState(true);
  const [keywordInput, setKeywordInput] = useState('');
  const [keywordSuggestions, setKeywordSuggestions] = useState([]);

  const [row2Focused, setRow2Focused] = useState(false);
  const [row4FromFocused, setRow4FromFocused] = useState(false);
  const [row4ToFocused, setRow4ToFocused] = useState(false);
  const [a3PostSwitchNoteVisible, setA3PostSwitchNoteVisible] = useState(false);
  const [a3SuggestionDecision, setA3SuggestionDecision] = useState('');
  const [row4ServiceMode, setRow4ServiceMode] = useState('');
  const [pauseAutoShiftNotice, setPauseAutoShiftNotice] = useState('');
  const [enforcedNoBreakPauseHours, setEnforcedNoBreakPauseHours] = useState(0);
  const [isQuestionFiveManuallyEdited, setIsQuestionFiveManuallyEdited] = useState(false);
  const [questionFiveManualReference, setQuestionFiveManualReference] = useState('');

  const steps = logbookEntryWizardSchema.steps;
  const dateStep = steps.find((step) => step.id === 'dateChoice');
  const entryTypeStep = steps.find((step) => step.id === 'entryType');
  const entryTypeOptions = configuredEntryTypeOptions;
  const allowedEntryTypes = useMemo(
    () => configuredEntryTypeOptions.map((option) => option.value),
    [configuredEntryTypeOptions],
  );

  const selectedEntryType = getSelectedEntryType(answers);
  const configuredEntryTypeMetaByValue = useMemo(
    () => new Map(entryTypeOptions.map((item) => [item.value, item])),
    [entryTypeOptions],
  );
  const selectedDate = getSelectedDate(answers);
  const selectedEntryTypeMeta = configuredEntryTypeMetaByValue.get(selectedEntryType);
  const responsibleQuestion = getResponsibleQuestion(selectedEntryType, selectedEntryTypeMeta);
  const timeQuestion = getDisplayedTimeQuestion(
    selectedEntryType,
    answers.entryVariant,
    getTimeQuestion(selectedEntryType, selectedEntryTypeMeta),
    selectedEntryTypeMeta,
  );
  const selectedEntryRule = getLogbookEntryTypeRule(selectedEntryType, selectedEntryTypeMeta);
  const keywordMode = configuredEntryTypesReady
    ? String(selectedEntryTypeMeta?.keywordMode || 'optional')
    : 'optional';
  const shouldShowKeywordRow = keywordMode !== 'hidden';
  const isKeywordRequired = keywordMode === 'required';
  const hoursQuestionMode = configuredEntryTypesReady
    ? String(selectedEntryTypeMeta?.hoursQuestionMode || 'required')
    : 'required';
  const isHoursQuestionHiddenByConfig = hoursQuestionMode === 'hidden';
  const shouldForceZeroHours = Boolean(selectedEntryRule.autoHoursZero || isHoursQuestionHiddenByConfig);
  const row4Mode = selectedEntryRule.row4Mode;
  const isSrhtBasicCourseWorkflow = isSrhtBasicCourseEntryType(selectedEntryType);
  const isSelectedTypeDayService = isConfiguredDayServiceEntryType(selectedEntryType, configuredEntryTypeMetaByValue);
  const serviceDurationLabel = getServiceDurationLabelForVariant(
    selectedEntryType,
    answers.entryVariant,
    configuredEntryTypeMetaByValue,
  );
  const responsiblePersonLabel = user?.full_name || user?.fullName || 'Unbekannt';
  const selectedDayServiceCodeGroup = getDayServiceCodeGroup(answers.entryVariant);
  const supportsA3Za3Selection = configuredEntryTypesReady
    ? Boolean(selectedEntryTypeMeta?.supportsA3Za3 ?? isSelectedTypeDayService)
    : Boolean(selectedEntryTypeMeta?.supportsA3Za3 ?? isSelectedTypeDayService);
  const supportsTGroupSelection = configuredEntryTypesReady
    ? Boolean(selectedEntryTypeMeta?.supportsTGroup ?? isSelectedTypeDayService)
    : Boolean(selectedEntryTypeMeta?.supportsTGroup ?? isSelectedTypeDayService);
  const supportsHonorarSelection = configuredEntryTypesReady
    ? Boolean(selectedEntryTypeMeta?.supportsHonorar ?? isSelectedTypeDayService)
    : Boolean(selectedEntryTypeMeta?.supportsHonorar ?? isSelectedTypeDayService);
  const shouldUseDayServiceCodeStep = Boolean(
    !isSrhtBasicCourseWorkflow
      && isConfiguredDayServiceEntryType(selectedEntryType, configuredEntryTypeMetaByValue)
      && (supportsA3Za3Selection || supportsTGroupSelection || supportsHonorarSelection),
  );
  const dayServiceCodeGroupCount = Number(supportsA3Za3Selection) + Number(supportsTGroupSelection) + Number(supportsHonorarSelection);
  const isDayServiceWizardFlow = Boolean(shouldUseDayServiceCodeStep && !isSrhtBasicCourseWorkflow);

  const sortedUsers = useMemo(() => sortResponsibleUsers(users), [users]);
  const currentUserId = Number(user?.id || 0);
  const canCreateTrainingEntries = Boolean(
    user
      && Array.isArray(user.roles)
      && (
        user.roles.includes('admin')
        || user.roles.includes('höhenretter')
        || user.roles.includes('einheitsführer')
        || user.roles.some((role) => typeof role === 'string' && role.startsWith('lehrhöhenretter'))
      ),
  );
  const canCreateSelectedEntry = canCreateTrainingEntries || (
    selectedEntryType === EQUIPMENT_CHECK_DAY_ENTRY_TYPE
      && hasEquipmentCheckDayEntryAccess(user?.roles)
  ) || (
    selectedEntryType === SRHT_BASIC_COURSE_ENTRY_TYPE
      && hasSrhtBasicCourseAccess(user?.roles)
  );
  const canReplicateToOtherLogbooks = Boolean(
    user
      && Array.isArray(user.roles)
      && (
        user.roles.includes('admin')
        || user.roles.includes('leiter')
        || user.roles.includes('einheitsführer')
        || user.roles.some((role) => typeof role === 'string' && role.startsWith('lehrhöhenretter'))
        || (selectedEntryType === EQUIPMENT_CHECK_DAY_ENTRY_TYPE && hasEquipmentCheckDayEntryAccess(user.roles))
      ),
  );

  const eligibleCopyUsers = useMemo(
    () => {
      const currentUserCandidate = users.find((candidate) => Number(candidate.id) === currentUserId)
        || (currentUserId > 0 ? {
          id: currentUserId,
          full_name: user?.full_name || user?.fullName || user?.username || 'Eigenes Logbuch',
          roles: user?.roles || [],
        } : null);
      const candidates = !isSrhtBasicCourseWorkflow && currentUserCandidate
        ? [
          currentUserCandidate,
          ...sortedUsers.filter((candidate) => Number(candidate.id) !== currentUserId),
        ]
        : sortedUsers;

      return candidates.filter((candidate) => {
      const candidateId = Number(candidate.id);
      const candidateRoles = Array.isArray(candidate.roles) ? candidate.roles : [];
      const isAdminCandidate = candidate.full_name === 'Administrator' || candidateRoles.includes('admin');
      const isCurrentUser = currentUserId > 0 && candidateId === currentUserId;

      if (isSrhtBasicCourseWorkflow) {
        return !isCurrentUser && isExclusiveApplicantUser(candidate);
      }

        return isCurrentUser || !isAdminCandidate;
      });
    },
    [sortedUsers, users, user, currentUserId, isSrhtBasicCourseWorkflow],
  );
  const allowOwnLogbookDeselection = canDeselectOwnLogbook(user?.roles, selectedEntryType);

  const selectedResponsibleCandidate = useMemo(
    () => sortedUsers.find((candidate) => String(candidate.id) === String(answers.responsibleId)) || null,
    [sortedUsers, answers.responsibleId],
  );
  const supportsExternalResponsible = selectedEntryType === EXTERNAL_TRAINING_ENTRY_TYPE;
  const selectedResponsibleMode = supportsExternalResponsible
    ? String(answers.responsibleMode || RESPONSIBLE_MODE_INTERNAL)
    : RESPONSIBLE_MODE_INTERNAL;
  const isExternalResponsibleMode = supportsExternalResponsible && selectedResponsibleMode === RESPONSIBLE_MODE_EXTERNAL;
  const externalResponsibleDisplayName = useMemo(
    () => formatResponsibleDisplayName(answers.responsibleNameSnapshot, answers.responsibleCompanySnapshot),
    [answers.responsibleCompanySnapshot, answers.responsibleNameSnapshot],
  );

  const isRow1Complete = Boolean(selectedDate);
  const isRow2Complete = Boolean(selectedEntryType);
  const isRow3Complete = selectedEntryRule.autoResponsibleCurrentUser
    ? currentUserId > 0
    : (isExternalResponsibleMode
      ? Boolean(String(answers.responsibleNameSnapshot || '').trim() && String(answers.responsibleCompanySnapshot || '').trim())
      : Boolean(answers.responsibleId));

  const isRow4VariantComplete = isDayServiceWizardFlow
    ? Boolean(row4ServiceMode)
    : (
      row4Mode === 'custom-only' || row4Mode === 'srht-basic-course'
        || (shouldUseDayServiceCodeStep && (row4Mode === 'a3-za3' || row4Mode === 'te1-flex-honorar'))
        ? true
        : Boolean(answers.entryVariant)
    );

  const isRow4TimeComplete = row4Mode === 'srht-basic-course'
    || (isDayServiceWizardFlow ? Boolean(answers.timeFrom && answers.timeTo) : Boolean(answers.timeFrom && answers.timeTo));
  const isRow4PauseComplete = !isDayServiceWizardFlow
    || row4ServiceMode === 'full-day'
    || Boolean(answers.pauseAnswer);
  const isRow4Complete = isRow4VariantComplete && isRow4TimeComplete;

  const isA3VariantEntry = row4Mode === 'a3-za3';

  const baseServiceDuration = useMemo(() => {
    if (!isRow4Complete) {
      return null;
    }

    return calculateRawServiceHours(answers.timeFrom, answers.timeTo);
  }, [isRow4Complete, answers.timeFrom, answers.timeTo]);

  const adjustedServiceDurationForCode = isRow4Complete && isRow4PauseComplete && baseServiceDuration !== null
    ? calculateDisplayedServiceHours(
      selectedEntryType,
      answers.entryVariant,
      answers.timeFrom,
      answers.timeTo,
      answers.pauseAnswer,
      enforcedNoBreakPauseHours,
      configuredEntryTypeMetaByValue,
    )
    : null;
  const shouldShowRow7A3Recommendation = Boolean(
    isDayServiceWizardFlow
      && row4ServiceMode === 'individual'
      && adjustedServiceDurationForCode !== null
      && adjustedServiceDurationForCode >= FULL_DAY_SUGGESTION_MIN_SERVICE_HOURS
      && adjustedServiceDurationForCode <= FULL_DAY_SUGGESTION_MAX_SERVICE_HOURS
      && !a3PostSwitchNoteVisible,
  );
  const isRow7A3RecommendationComplete = !shouldShowRow7A3Recommendation
    || a3SuggestionDecision === 'ja'
    || a3SuggestionDecision === 'nein';
  const isRow5Complete = isSrhtBasicCourseWorkflow || shouldForceZeroHours
    ? true
    : hasHoursValue(answers.hours);
  const isKeywordRowComplete = !shouldShowKeywordRow || !isKeywordRequired || (Array.isArray(answers.keywords) && answers.keywords.length > 0);
  const isQuestionFiveComplete = isQuestionFiveSequenceComplete({
    isRow5Complete,
    shouldShowKeywordRow,
    isKeywordRowComplete,
  });
  const shouldShowRow6 = Boolean(
    !isDayServiceWizardFlow
      && !isSrhtBasicCourseWorkflow
      && isQuestionFiveComplete
      && usesPauseQuestion(selectedEntryType, answers.entryVariant, configuredEntryTypeMetaByValue)
      && (shouldUseDayServiceCodeStep || isCustomPeriodVariant(selectedEntryType, answers.entryVariant))
      && baseServiceDuration !== null
      && baseServiceDuration >= 6,
  );
  const hasMultipleDayServiceCodeChoices = Boolean(dayServiceCodeGroupCount > 1);
  const shouldShowRow7Code = Boolean(
    shouldUseDayServiceCodeStep
      && isRow4PauseComplete
      && isRow4Complete
      && isRow7A3RecommendationComplete
      && isQuestionFiveComplete
      && hasMultipleDayServiceCodeChoices,
  );
  const isRow6Complete = shouldShowRow7Code
    ? Boolean(selectedDayServiceCodeGroup)
    : (!isDayServiceWizardFlow || Boolean(answers.entryVariant));

  const shouldShowRow7 = Boolean(
    canReplicateToOtherLogbooks
      && (isSrhtBasicCourseWorkflow ? isRow3Complete : true)
      && isRow4Complete
      && isRow4PauseComplete
      && isRow7A3RecommendationComplete
      && isQuestionFiveComplete
      && isRow6Complete,
  );
  const isRow7Complete = !shouldShowRow7
    || (isSrhtBasicCourseWorkflow
      ? (Array.isArray(answers.copyUserIds) && answers.copyUserIds.length > 0)
      : answers.copyToOthers === 'nein'
        || (answers.copyToOthers === 'ja' && Array.isArray(answers.copyUserIds) && answers.copyUserIds.length > 0));
  const shouldShowRow8 = shouldShowIncidentQuestion({
    isRow3Complete,
    isRow4Complete,
    isRow4PauseComplete,
    isRow7A3RecommendationComplete,
    isQuestionFiveComplete,
    isRow6Complete,
    isRow7Complete,
    isSrhtBasicCourseWorkflow,
  });
  const hasIncidentAnswer = incidentAnswer === 'ja' || incidentAnswer === 'nein';
  const isIncidentDetailsComplete = incidentAnswer !== 'ja' || isIncidentFormValid(incidentFormState);
  const isRow8Complete = !shouldShowRow8 || (hasIncidentAnswer && isIncidentDetailsComplete);
  const shouldShowRow9 = shouldShowRow8 && isRow8Complete && selectedEntryRule.askPdfProtocol;
  const isRow9Complete = !shouldShowRow9 || answers.pdfProtocolAnswer === 'ja' || answers.pdfProtocolAnswer === 'nein';

  const codeQuestionNumber = isDayServiceWizardFlow ? 6 : 7;
  const copyQuestionNumber = isDayServiceWizardFlow
    ? (6 + Number(shouldShowRow7Code))
    : (7 + Number(shouldShowRow7A3Recommendation) + Number(shouldShowRow7Code));
  const incidentQuestionNumber = getIncidentQuestionNumber(copyQuestionNumber, shouldShowRow7);
  const pdfQuestionNumber = getPdfQuestionNumber(incidentQuestionNumber, shouldShowRow8);

  const adjustedServiceDuration = useMemo(() => {
    if (!isRow4Complete || !isRow4PauseComplete || baseServiceDuration === null) {
      return null;
    }

    return calculateDisplayedServiceHours(
      selectedEntryType,
      answers.entryVariant,
      answers.timeFrom,
      answers.timeTo,
      answers.pauseAnswer,
      enforcedNoBreakPauseHours,
      configuredEntryTypeMetaByValue,
    );
  }, [
    isRow4Complete,
    baseServiceDuration,
    selectedEntryType,
    answers.entryVariant,
    answers.timeFrom,
    answers.timeTo,
    answers.pauseAnswer,
    enforcedNoBreakPauseHours,
    configuredEntryTypeMetaByValue,
    isRow4PauseComplete,
  ]);

  const suggestedQuestionFiveHours = useMemo(() => {
    if (!isRow4Complete || !isRow4PauseComplete || adjustedServiceDuration === null) {
      return ZERO_DURATION;
    }

    return getQuestionFiveSuggestedHours(adjustedServiceDuration);
  }, [isRow4Complete, isRow4PauseComplete, adjustedServiceDuration]);

  const displayedQuestionFiveHours = useMemo(() => {
    if (answers.hours !== '') {
      return normalizeDurationInput(answers.hours) || ZERO_DURATION;
    }

    return suggestedQuestionFiveHours || ZERO_DURATION;
  }, [answers.hours, suggestedQuestionFiveHours]);

  const displayedQuestionFiveParts = useMemo(
    () => getDurationParts(displayedQuestionFiveHours),
    [displayedQuestionFiveHours],
  );

  const showQuestionFiveSyncNotice = useMemo(() => shouldShowQuestionFiveSyncNotice({
    isManuallyEdited: isQuestionFiveManuallyEdited,
    manualReference: questionFiveManualReference,
    suggestedHours: suggestedQuestionFiveHours,
  }), [isQuestionFiveManuallyEdited, questionFiveManualReference, suggestedQuestionFiveHours]);

  useEffect(() => {
    if (!answers.entryType) {
      return;
    }

    if (!allowedEntryTypes.includes(answers.entryType)) {
      setAnswers((prev) => ({
        ...prev,
        entryType: '',
      }));
    }
  }, [allowedEntryTypes, answers.entryType]);

  useEffect(() => {
    if (!selectedEntryRule.autoResponsibleCurrentUser || currentUserId <= 0) {
      return;
    }

    const currentUserIdString = String(currentUserId);
    if (answers.responsibleId === currentUserIdString && normalizeDurationInput(answers.hours) === ZERO_DURATION) {
      return;
    }

    setAnswers((prev) => ({
      ...prev,
      responsibleId: currentUserIdString,
      hours: ZERO_DURATION,
    }));
  }, [selectedEntryRule.autoResponsibleCurrentUser, currentUserId, answers.responsibleId, answers.hours]);

  useEffect(() => {
    if (!shouldForceZeroHours) {
      return;
    }

    if (normalizeDurationInput(answers.hours) === ZERO_DURATION) {
      return;
    }

    setAnswers((prev) => ({
      ...prev,
      hours: ZERO_DURATION,
    }));
  }, [shouldForceZeroHours, answers.hours]);

  useEffect(() => {
    if (shouldForceZeroHours || isSrhtBasicCourseWorkflow) {
      if (isQuestionFiveManuallyEdited) {
        setIsQuestionFiveManuallyEdited(false);
      }
      if (questionFiveManualReference) {
        setQuestionFiveManualReference('');
      }
      return;
    }

    if (!isRow4Complete || !isRow4PauseComplete || adjustedServiceDuration === null) {
      return;
    }

    if (isQuestionFiveManuallyEdited) {
      return;
    }

    if (normalizeDurationInput(answers.hours) === suggestedQuestionFiveHours) {
      return;
    }

    setAnswers((prev) => ({
      ...prev,
      hours: suggestedQuestionFiveHours,
    }));
    setQuestionFiveManualReference(suggestedQuestionFiveHours);
  }, [
    shouldForceZeroHours,
    isSrhtBasicCourseWorkflow,
    isQuestionFiveManuallyEdited,
    questionFiveManualReference,
    isRow4Complete,
    isRow4PauseComplete,
    adjustedServiceDuration,
    answers.hours,
    suggestedQuestionFiveHours,
  ]);

  useEffect(() => {
    if (!shouldUseDayServiceCodeStep || !isRow4Complete || !isRow4PauseComplete || !isQuestionFiveComplete) {
      return;
    }

    let forcedGroup = '';
    if (supportsA3Za3Selection && dayServiceCodeGroupCount === 1) {
      forcedGroup = 'A';
    } else if (supportsTGroupSelection && dayServiceCodeGroupCount === 1) {
      forcedGroup = 'T';
    } else if (supportsHonorarSelection && dayServiceCodeGroupCount === 1) {
      forcedGroup = 'HONORAR';
    }

    if (!forcedGroup) {
      return;
    }

    const forcedVariant = resolveDayServiceVariantForGroup(forcedGroup, adjustedServiceDurationForCode);
    if (!forcedVariant) {
      return;
    }

    setAnswers((prev) => (prev.entryVariant === forcedVariant ? prev : { ...prev, entryVariant: forcedVariant }));
  }, [
    shouldUseDayServiceCodeStep,
    isRow4Complete,
    isRow4PauseComplete,
    isQuestionFiveComplete,
    supportsA3Za3Selection,
    supportsTGroupSelection,
    supportsHonorarSelection,
    dayServiceCodeGroupCount,
    adjustedServiceDurationForCode,
  ]);

  useEffect(() => {
    if (!shouldUseDayServiceCodeStep || !isRow4Complete || !isRow4PauseComplete) {
      return;
    }

    const currentGroup = getDayServiceCodeGroup(answers.entryVariant);
    if (!currentGroup) {
      return;
    }

    const nextVariant = resolveDayServiceVariantForGroup(currentGroup, adjustedServiceDurationForCode);
    if (!nextVariant || nextVariant === answers.entryVariant) {
      return;
    }

    setAnswers((prev) => ({
      ...prev,
      entryVariant: nextVariant,
    }));
  }, [
    shouldUseDayServiceCodeStep,
    isRow4Complete,
    isRow4PauseComplete,
    answers.entryVariant,
    adjustedServiceDurationForCode,
  ]);

  const derivedPrepTime = useMemo(() => {
    if (!isRow5Complete || baseServiceDuration === null) {
      return '';
    }

    const hoursValue = durationStringToDecimalHours(normalizeDurationInput(answers.hours));
    if (!Number.isFinite(hoursValue)) {
      return '';
    }

    const effectiveServiceHours = calculateDisplayedServiceHours(
      selectedEntryType,
      answers.entryVariant,
      answers.timeFrom,
      answers.timeTo,
      answers.pauseAnswer,
      enforcedNoBreakPauseHours,
      configuredEntryTypeMetaByValue,
    );

    if (effectiveServiceHours === null) {
      return '';
    }

    return Math.max(0, Number((effectiveServiceHours - hoursValue).toFixed(2)));
  }, [
    isRow5Complete,
    baseServiceDuration,
    selectedEntryType,
    answers.entryVariant,
    answers.timeFrom,
    answers.timeTo,
    answers.hours,
    answers.pauseAnswer,
    enforcedNoBreakPauseHours,
    configuredEntryTypeMetaByValue,
  ]);

  const getInheritedCopyValues = useCallback(() => ({
    timeFrom: answers.timeFrom || '',
    timeTo: answers.timeTo || '',
    hours: answers.hours === '' ? '' : normalizeDurationInput(answers.hours),
    entryVariant: answers.entryVariant || '',
  }), [answers.timeFrom, answers.timeTo, answers.hours, answers.entryVariant]);

  useEffect(() => {
    if (isSrhtBasicCourseWorkflow) {
      return;
    }

    if (!canReplicateToOtherLogbooks) {
      return;
    }

    setAnswers((prev) => {
      const allowedIds = new Set(eligibleCopyUsers.map((candidate) => String(candidate.id)));
      const previousIds = Array.isArray(prev.copyUserIds) ? prev.copyUserIds.map(String) : [];
      const nextIds = previousIds.filter((id) => allowedIds.has(id));

      const currentId = String(currentUserId || '');
      if (
        prev.copyToOthers === 'ja'
        && !allowOwnLogbookDeselection
        && currentId
        && allowedIds.has(currentId)
        && !nextIds.includes(currentId)
      ) {
        nextIds.unshift(currentId);
      }

      let changed = nextIds.length !== previousIds.length;
      const nextOverrides = { ...(prev.copyUserOverrides || {}) };

      Object.keys(nextOverrides).forEach((key) => {
        if (!allowedIds.has(String(key)) || Number(key) === currentUserId) {
          delete nextOverrides[key];
          changed = true;
        }
      });

      if (!changed) {
        return prev;
      }

      return {
        ...prev,
        copyUserIds: nextIds,
        copyUserOverrides: nextOverrides,
      };
    });
  }, [
    allowOwnLogbookDeselection,
    canReplicateToOtherLogbooks,
    currentUserId,
    eligibleCopyUsers,
    isSrhtBasicCourseWorkflow,
  ]);

  useEffect(() => {
    if (isSrhtBasicCourseWorkflow) {
      return;
    }

    if (answers.copyToOthers !== 'ja') {
      return;
    }

    setAnswers((prev) => {
      const inheritedValues = {
        timeFrom: prev.timeFrom || '',
        timeTo: prev.timeTo || '',
        hours: prev.hours === '' ? '' : normalizeDurationInput(prev.hours),
        entryVariant: prev.entryVariant || '',
      };

      const nextOverrides = { ...(prev.copyUserOverrides || {}) };
      let changed = false;

      (prev.copyUserIds || []).forEach((userId) => {
        const key = String(userId);
        if (Number(userId) === currentUserId) {
          if (nextOverrides[key]) {
            delete nextOverrides[key];
            changed = true;
          }
          return;
        }

        const currentEntry = nextOverrides[key];
        if (!currentEntry) {
          nextOverrides[key] = {
            override: false,
            expanded: true,
            showTimeInputs: false,
            ...inheritedValues,
          };
          changed = true;
          return;
        }

        if (!currentEntry.override) {
          if (
            currentEntry.timeFrom !== inheritedValues.timeFrom
            || currentEntry.timeTo !== inheritedValues.timeTo
            || currentEntry.hours !== inheritedValues.hours
            || currentEntry.entryVariant !== inheritedValues.entryVariant
          ) {
            nextOverrides[key] = {
              ...currentEntry,
              ...inheritedValues,
            };
            changed = true;
          }
        }
      });

      if (!changed) {
        return prev;
      }

      return {
        ...prev,
        copyUserOverrides: nextOverrides,
      };
    });
  }, [
    answers.copyToOthers,
    answers.copyUserIds,
    answers.timeFrom,
    answers.timeTo,
    answers.hours,
    answers.entryVariant,
    currentUserId,
    isSrhtBasicCourseWorkflow,
  ]);

  const preparedUserEntries = useMemo(() => {
    if (isSrhtBasicCourseWorkflow) {
      return [];
    }

    if (answers.copyToOthers !== 'ja' || !Array.isArray(answers.copyUserIds) || answers.copyUserIds.length === 0 || !currentUserId) {
      return [];
    }

    return answers.copyUserIds.map((userId) => {
      const key = String(userId);
      if (Number(userId) === currentUserId) {
        return {
          userId: currentUserId,
          timeFrom: answers.timeFrom || '',
          timeTo: answers.timeTo || '',
          hours: answers.hours === '' ? '' : normalizeDurationInput(answers.hours),
          entryVariant: answers.entryVariant || '',
          entryType: getEffectiveEntryTypeForVariant(selectedEntryType, answers.entryVariant),
        };
      }

      const override = answers.copyUserOverrides?.[key];
      const inheritedValues = getInheritedCopyValues();
      const values = override && override.override
        ? {
          timeFrom: override.timeFrom || inheritedValues.timeFrom,
          timeTo: override.timeTo || inheritedValues.timeTo,
          hours: override.hours === '' ? '' : (override.hours ?? inheritedValues.hours),
          entryVariant: override.entryVariant || inheritedValues.entryVariant,
        }
        : {
          ...inheritedValues,
          entryVariant: inheritedValues.entryVariant,
        };

      return {
        userId: Number(userId),
        timeFrom: values.timeFrom,
        timeTo: values.timeTo,
        hours: values.hours,
        entryVariant: values.entryVariant,
        entryType: getEffectiveEntryTypeForVariant(selectedEntryType, values.entryVariant),
      };
    });
  }, [
    answers.copyToOthers,
    answers.copyUserIds,
    answers.copyUserOverrides,
    answers.timeFrom,
    answers.timeTo,
    answers.hours,
    answers.entryVariant,
    currentUserId,
    getInheritedCopyValues,
    isSrhtBasicCourseWorkflow,
    selectedEntryType,
  ]);

  const handleCopyDecisionChange = (decision) => {
    if (isSrhtBasicCourseWorkflow) {
      return;
    }

    setAnswers((prev) => ({
      ...prev,
      copyToOthers: decision,
      copyUserIds: decision === 'ja'
        ? initializeLogbookTargetSelection(prev.copyUserIds, currentUserId)
        : [],
      copyUserOverrides: decision === 'ja' ? prev.copyUserOverrides : {},
    }));

    closeFormIfOpen();
  };

  const handleCopyUserToggle = (targetUserId) => {
    if (isSrhtBasicCourseWorkflow) {
      setAnswers((prev) => {
        const idString = String(targetUserId);
        const currentIds = Array.isArray(prev.copyUserIds) ? prev.copyUserIds.map(String) : [];
        const isCurrentlySelected = currentIds.includes(idString);

        return {
          ...prev,
          copyToOthers: 'ja',
          copyUserIds: isCurrentlySelected
            ? currentIds.filter((id) => id !== idString)
            : [...currentIds, idString],
          copyUserOverrides: {},
        };
      });

      closeFormIfOpen();
      return;
    }

    setAnswers((prev) => {
      const idString = String(targetUserId);
      const currentIds = Array.isArray(prev.copyUserIds) ? prev.copyUserIds.map(String) : [];
      const isCurrentlySelected = currentIds.includes(idString);
      const toggledSelection = toggleLogbookTargetSelection({
        selectedUserIds: currentIds,
        userOverrides: prev.copyUserOverrides,
        targetUserId,
        currentUserId,
        allowOwnDeselection: allowOwnLogbookDeselection,
      });

      if (!isCurrentlySelected) {
        if (Number(targetUserId) !== currentUserId) {
          toggledSelection.userOverrides[idString] = {
            override: false,
            expanded: true,
            showTimeInputs: false,
            ...getInheritedCopyValues(),
            entryVariant: prev.entryVariant || '',
          };
        }

        return {
          ...prev,
          copyUserIds: toggledSelection.selectedUserIds,
          copyUserOverrides: toggledSelection.userOverrides,
        };
      }

      return {
        ...prev,
        copyUserIds: toggledSelection.selectedUserIds,
        copyUserOverrides: toggledSelection.userOverrides,
      };
    });

    closeFormIfOpen();
  };

  const handleCopyUserExpandedToggle = (targetUserId) => {
    setAnswers((prev) => {
      const key = String(targetUserId);
      const currentEntry = prev.copyUserOverrides?.[key];
      if (!currentEntry) {
        return prev;
      }

      return {
        ...prev,
        copyUserOverrides: {
          ...(prev.copyUserOverrides || {}),
          [key]: {
            ...currentEntry,
            expanded: !currentEntry.expanded,
          },
        },
      };
    });
  };

  const handleCopyUserOverrideToggle = (targetUserId) => {
    setAnswers((prev) => {
      const key = String(targetUserId);
      const inheritedValues = getInheritedCopyValues();
      const currentEntry = prev.copyUserOverrides?.[key] || {
        override: false,
        expanded: true,
        showTimeInputs: false,
        ...inheritedValues,
        entryVariant: prev.entryVariant || '',
      };
      const enabled = !currentEntry.override;

      return {
        ...prev,
        copyUserOverrides: {
          ...(prev.copyUserOverrides || {}),
          [key]: {
            ...currentEntry,
            override: enabled,
            expanded: true,
            showTimeInputs: enabled,
            timeFrom: enabled ? (currentEntry.timeFrom || inheritedValues.timeFrom) : inheritedValues.timeFrom,
            timeTo: enabled ? (currentEntry.timeTo || inheritedValues.timeTo) : inheritedValues.timeTo,
            hours: enabled ? (currentEntry.hours === '' ? '' : (currentEntry.hours || inheritedValues.hours)) : inheritedValues.hours,
            entryVariant: enabled ? (currentEntry.entryVariant || prev.entryVariant || '') : (prev.entryVariant || ''),
          },
        },
      };
    });

    closeFormIfOpen();
  };

  const handleCopyUserFieldChange = (targetUserId, field, value) => {
    setAnswers((prev) => {
      const key = String(targetUserId);
      const inheritedValues = getInheritedCopyValues();
      const currentEntry = prev.copyUserOverrides?.[key] || {
        override: true,
        expanded: true,
        showTimeInputs: true,
        ...inheritedValues,
        entryVariant: prev.entryVariant || '',
      };

      return {
        ...prev,
        copyUserOverrides: {
          ...(prev.copyUserOverrides || {}),
          [key]: {
            ...currentEntry,
            override: true,
            expanded: true,
            showTimeInputs: true,
            [field]: value,
          },
        },
      };
    });

    closeFormIfOpen();
  };

  const handleCopyUserCodeGroupChange = (targetUserId, group, serviceDuration) => {
    const nextVariant = resolveDayServiceVariantForGroup(group, serviceDuration);
    if (!nextVariant) {
      return;
    }

    handleCopyUserFieldChange(targetUserId, 'entryVariant', nextVariant);
  };

  const handleCopyUserVariantSelection = (targetUserId, entryVariant) => {
    setAnswers((prev) => {
      const key = String(targetUserId);
      const inheritedValues = {
        timeFrom: prev.timeFrom || '',
        timeTo: prev.timeTo || '',
        hours: prev.hours === '' ? '' : normalizeDurationInput(prev.hours),
        entryVariant: prev.entryVariant || '',
      };
      const currentEntry = prev.copyUserOverrides?.[key] || {
        override: true,
        expanded: true,
        showTimeInputs: false,
        ...inheritedValues,
      };
      const defaultTimes = getDefaultDayEntryTimes();
      const flexEnabled = isDayEntryFlexEnabled(currentEntry.entryVariant || inheritedValues.entryVariant);
      const nextVariant = buildDayEntryVariant(entryVariant, flexEnabled);

      const nextEntry = {
        ...currentEntry,
        override: true,
        expanded: true,
        showTimeInputs: flexEnabled,
        entryVariant: nextVariant,
        hours: currentEntry.hours === ''
          ? ''
          : normalizeDurationInput(currentEntry.hours || inheritedValues.hours),
      };

      if (flexEnabled) {
        nextEntry.timeFrom = currentEntry.timeFrom || inheritedValues.timeFrom || defaultTimes.timeFrom;
        nextEntry.timeTo = currentEntry.timeTo || inheritedValues.timeTo || defaultTimes.timeTo;
      } else {
        nextEntry.timeFrom = defaultTimes.timeFrom;
        nextEntry.timeTo = defaultTimes.timeTo;
      }

      return {
        ...prev,
        copyUserOverrides: {
          ...(prev.copyUserOverrides || {}),
          [key]: nextEntry,
        },
      };
    });

    closeFormIfOpen();
  };

  const handleCopyUserTimeAdjustToggle = (targetUserId) => {
    setAnswers((prev) => {
      const key = String(targetUserId);
      const inheritedValues = {
        timeFrom: prev.timeFrom || '',
        timeTo: prev.timeTo || '',
        hours: prev.hours === '' ? '' : normalizeDurationInput(prev.hours),
        entryVariant: prev.entryVariant || '',
      };
      const currentEntry = prev.copyUserOverrides?.[key] || {
        override: false,
        expanded: true,
        showTimeInputs: false,
        ...inheritedValues,
      };
      const defaultTimes = getDefaultDayEntryTimes();
      const effectiveVariant = currentEntry.override
        ? (currentEntry.entryVariant || inheritedValues.entryVariant)
        : inheritedValues.entryVariant;
      const baseVariant = getDayEntryBaseVariant(effectiveVariant) || ENTRY_VARIANT_TE1;
      const nextShowTimeInputs = !isDayEntryFlexEnabled(effectiveVariant);
      const nextVariant = buildDayEntryVariant(baseVariant, nextShowTimeInputs);

      return {
        ...prev,
        copyUserOverrides: {
          ...(prev.copyUserOverrides || {}),
          [key]: {
            ...currentEntry,
            override: true,
            expanded: true,
            showTimeInputs: nextShowTimeInputs,
            entryVariant: nextVariant,
            timeFrom: nextShowTimeInputs
              ? (currentEntry.timeFrom || inheritedValues.timeFrom || defaultTimes.timeFrom)
              : defaultTimes.timeFrom,
            timeTo: nextShowTimeInputs
              ? (currentEntry.timeTo || inheritedValues.timeTo || defaultTimes.timeTo)
              : defaultTimes.timeTo,
            hours: currentEntry.hours === ''
              ? ''
              : normalizeDurationInput(currentEntry.hours || inheritedValues.hours),
          },
        },
      };
    });

    closeFormIfOpen();
  };

  const getCopyUserServiceDuration = (targetUserId, overrideEnabled = false) => {
    const key = String(targetUserId);
    const override = answers.copyUserOverrides?.[key];
    const inheritedValues = getInheritedCopyValues();
    const values = override && override.override
      ? {
        timeFrom: override.timeFrom ?? inheritedValues.timeFrom,
        timeTo: override.timeTo ?? inheritedValues.timeTo,
        entryVariant: override.entryVariant || inheritedValues.entryVariant,
      }
      : {
        ...inheritedValues,
        entryVariant: inheritedValues.entryVariant,
      };

    return calculateDisplayedServiceHours(
      selectedEntryType,
      values.entryVariant,
      values.timeFrom,
      values.timeTo,
      answers.pauseAnswer,
      enforcedNoBreakPauseHours,
      configuredEntryTypeMetaByValue,
    );
  };

  const refreshConfiguredEntryTypes = useCallback(async () => {
    try {
      const options = await getTrainingEntryTypes({ ts: Date.now() });
      setConfiguredEntryTypeOptions(options);
      setConfiguredEntryTypesReady(true);
    } catch (error) {
      console.error('Load configured entry types failed:', error);
    }
  }, []);

  useEffect(() => {
    let mounted = true;

    const loadUsers = async () => {
      setUsersLoading(true);
      setUsersError('');
      try {
        const response = await client.get('/api/admin/users');
        if (mounted) {
          const selectableUsers = (response.data?.users || []).filter((entry) => {
            const roles = Array.isArray(entry?.roles) ? entry.roles : [];
            return !roles.includes('extern_partner');
          });
          setUsers(selectableUsers);
        }
      } catch {
        if (mounted) {
          setUsers([]);
          setUsersError('Die Benutzerliste konnte nicht geladen werden.');
        }
      } finally {
        if (mounted) {
          setUsersLoading(false);
        }
      }
    };

    Promise.all([loadUsers(), refreshConfiguredEntryTypes()]);

    return () => {
      mounted = false;
    };
  }, [refreshConfiguredEntryTypes]);

  useEffect(() => {
    if (!selectedEntryType) {
      return;
    }

    refreshConfiguredEntryTypes();
  }, [selectedEntryType, refreshConfiguredEntryTypes]);

  useEffect(() => {
    let mounted = true;

    const loadIncidentMeta = async () => {
      setIncidentOptionsLoading(true);
      setIncidentOptionsError('');
      try {
        const [optionsResponse, capabilitiesResponse] = await Promise.all([
          getFeedbackOptions(),
          getFeedbackCapabilities(),
        ]);

        if (!mounted) {
          return;
        }

        setIncidentOptions({
          entryTypes: optionsResponse.data?.entryTypes || [],
          responsibles: optionsResponse.data?.responsibles || [],
        });

        const incidentCapability = (capabilitiesResponse.data?.topics || [])
          .find((topic) => topic.key === 'zwischenfaelle');
        setIncidentCanCreate(Boolean(incidentCapability?.canCreate));
      } catch {
        if (!mounted) {
          return;
        }

        setIncidentOptions({ entryTypes: [], responsibles: [] });
        setIncidentCanCreate(false);
        setIncidentOptionsError('Das Formular für Zwischenfälle konnte nicht geladen werden.');
      } finally {
        if (mounted) {
          setIncidentOptionsLoading(false);
        }
      }
    };

    loadIncidentMeta();

    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    const query = normalizeKeywordToken(keywordInput);
    if (!query || query.length < 2 || !shouldShowKeywordRow) {
      setKeywordSuggestions([]);
      return undefined;
    }

    let cancelled = false;
    const timeoutId = window.setTimeout(async () => {
      try {
        const response = await client.get('/api/training/keyword-suggestions', { params: { q: query } });
        if (cancelled) {
          return;
        }

        const suggestions = Array.isArray(response.data?.suggestions)
          ? response.data.suggestions
            .map((item) => normalizeKeywordToken(item?.label || ''))
            .filter(Boolean)
          : [];

        setKeywordSuggestions(normalizeKeywordList(suggestions));
      } catch {
        if (!cancelled) {
          setKeywordSuggestions([]);
        }
      }
    }, 180);

    return () => {
      cancelled = true;
      window.clearTimeout(timeoutId);
    };
  }, [keywordInput, shouldShowKeywordRow]);

  useEffect(() => {
    if (incidentAnswer !== 'ja') {
      return;
    }

    setIncidentFormState((prev) => ({
      ...prev,
      eventDate: prev.eventDate || selectedDate || '',
      entryType: prev.entryType || selectedEntryType || '',
      responsibleId: prev.responsibleId || answers.responsibleId || (currentUserId > 0 ? String(currentUserId) : ''),
    }));
  }, [incidentAnswer, selectedDate, selectedEntryType, answers.responsibleId, currentUserId]);

  const closeFormIfOpen = () => {
    setShowForm(false);
  };

  const handleDateChoiceChange = (value) => {
    const today = getTodayIsoDate();

    setAnswers((prev) => {
      if (value === 'today') {
        return {
          ...prev,
          dateChoice: 'today',
          date: today,
        };
      }

      return {
        ...prev,
        dateChoice: 'custom',
        date: prev.date || today,
      };
    });

    closeFormIfOpen();
    setA3PostSwitchNoteVisible(false);
  };

  const handleCustomDateChange = (value) => {
    setAnswers((prev) => {
      return {
        ...prev,
        date: value || '',
      };
    });

    closeFormIfOpen();
    setA3PostSwitchNoteVisible(false);
  };

  const handleEntryTypeChange = (value) => {
    setAnswers((prev) => {
      const nextRule = getLogbookEntryTypeRule(value, configuredEntryTypeMetaByValue.get(value));
      const resolvedPreviousRule = getLogbookEntryTypeRule(prev.entryType, configuredEntryTypeMetaByValue.get(prev.entryType));
      const mode = nextRule.row4Mode;
      const previousHoursQuestionMode = configuredEntryTypesReady
        ? String(configuredEntryTypeMetaByValue.get(prev.entryType)?.hoursQuestionMode || 'required')
        : 'required';
      const nextHoursQuestionMode = configuredEntryTypesReady
        ? String(configuredEntryTypeMetaByValue.get(value)?.hoursQuestionMode || 'required')
        : 'required';
      const previousForceZeroHours = Boolean(resolvedPreviousRule.autoHoursZero || previousHoursQuestionMode === 'hidden');
      const nextForceZeroHours = Boolean(nextRule.autoHoursZero || nextHoursQuestionMode === 'hidden');

      let nextVariant = prev.entryVariant;
      let nextTimeFrom = prev.timeFrom;
      let nextTimeTo = prev.timeTo;
      let nextResponsibleId = prev.responsibleId;
      let nextHours = prev.hours;
      const keepExternalResponsibleDraft = value === EXTERNAL_TRAINING_ENTRY_TYPE
        && prev.entryType === EXTERNAL_TRAINING_ENTRY_TYPE
        && prev.responsibleMode === RESPONSIBLE_MODE_EXTERNAL;
      const nextResponsibleMode = value === EXTERNAL_TRAINING_ENTRY_TYPE
        ? String(prev.responsibleMode || RESPONSIBLE_MODE_INTERNAL)
        : RESPONSIBLE_MODE_INTERNAL;

      if (mode === 'srht-basic-course') {
        nextVariant = '';
        nextTimeFrom = '';
        nextTimeTo = '';
        nextHours = ZERO_DURATION;
      } else if (mode === 'preset-or-custom') {
        if (prev.entryVariant === 'custom24h' || prev.entryVariant === 'za3' || prev.entryVariant === 'customOnly') {
          nextVariant = 'custom24h';
        } else if (prev.entryVariant === 'preset24h') {
          nextVariant = 'preset24h';
          nextTimeFrom = '09:00';
          nextTimeTo = '12:30';
        } else {
          nextVariant = '';
          nextTimeFrom = '';
          nextTimeTo = '';
        }
      } else if (mode === 'custom-only') {
        nextVariant = 'customOnly';
      } else if (mode === 'te1-flex-honorar') {
        const previousBaseVariant = getDayEntryBaseVariant(prev.entryVariant);
        if (previousBaseVariant) {
          const previousFlexEnabled = isDayEntryFlexEnabled(prev.entryVariant);
          const defaultTimes = getDefaultDayEntryTimes();
          nextVariant = buildDayEntryVariant(previousBaseVariant, previousFlexEnabled);
          if (previousFlexEnabled) {
            nextTimeFrom = prev.timeFrom || defaultTimes.timeFrom;
            nextTimeTo = prev.timeTo || defaultTimes.timeTo;
          } else {
            nextTimeFrom = defaultTimes.timeFrom;
            nextTimeTo = defaultTimes.timeTo;
          }
        } else {
          nextVariant = '';
          nextTimeFrom = '';
          nextTimeTo = '';
        }
      } else {
        if (prev.entryVariant === 'za3' || prev.entryVariant === 'custom24h' || prev.entryVariant === 'customOnly') {
          nextVariant = 'za3';
        } else if (prev.entryVariant === 'a3') {
          nextVariant = 'a3';
          nextTimeFrom = A3_DEFAULT_TIME_FROM;
          nextTimeTo = A3_DEFAULT_TIME_TO;
        } else {
          nextVariant = '';
          nextTimeFrom = '';
          nextTimeTo = '';
        }
      }

      const nextPauseAnswer = getValidPauseAnswer(value, nextVariant, prev.pauseAnswer, configuredEntryTypeMetaByValue);

      if (nextRule.autoResponsibleCurrentUser) {
        nextResponsibleId = currentUserId > 0 ? String(currentUserId) : '';
      } else if (resolvedPreviousRule.autoResponsibleCurrentUser) {
        nextResponsibleId = '';
      }

      if (nextForceZeroHours) {
        nextHours = ZERO_DURATION;
      } else if (previousForceZeroHours && normalizeDurationInput(prev.hours) === ZERO_DURATION) {
        nextHours = '';
      }

      return {
        ...prev,
        entryType: value,
        responsibleId: nextResponsibleId,
        responsibleMode: nextResponsibleMode,
        responsibleNameSnapshot: keepExternalResponsibleDraft ? prev.responsibleNameSnapshot : '',
        responsibleCompanySnapshot: keepExternalResponsibleDraft ? prev.responsibleCompanySnapshot : '',
        responsibleIsLehrhSnapshot: keepExternalResponsibleDraft ? Boolean(prev.responsibleIsLehrhSnapshot) : false,
        entryVariant: nextVariant,
        timeFrom: nextTimeFrom,
        timeTo: nextTimeTo,
        hours: nextHours,
        keywords: [],
        pauseAnswer: nextPauseAnswer,
        copyToOthers: mode === 'srht-basic-course' ? 'ja' : (resolvedPreviousRule.row4Mode === 'srht-basic-course' ? '' : prev.copyToOthers),
        copyUserIds: mode === 'srht-basic-course' ? [] : (resolvedPreviousRule.row4Mode === 'srht-basic-course' ? [] : prev.copyUserIds),
        copyUserOverrides: mode === 'srht-basic-course' ? {} : (resolvedPreviousRule.row4Mode === 'srht-basic-course' ? {} : prev.copyUserOverrides),
        pdfProtocolAnswer: mode === 'srht-basic-course' ? 'nein' : (resolvedPreviousRule.row4Mode === 'srht-basic-course' ? '' : prev.pdfProtocolAnswer),
      };
    });

    closeFormIfOpen();
    setRow4ServiceMode('');
    setA3SuggestionDecision('');
    setA3PostSwitchNoteVisible(false);
    setPauseAutoShiftNotice('');
    setEnforcedNoBreakPauseHours(0);
    setIsQuestionFiveManuallyEdited(false);
    setQuestionFiveManualReference('');
  };

  const handleResponsibleModeChange = useCallback((mode) => {
    setAnswers((prev) => ({
      ...prev,
      responsibleMode: mode,
      responsibleId: mode === RESPONSIBLE_MODE_EXTERNAL ? '' : prev.responsibleId,
    }));
  }, []);

  const handleRow4ServiceModeChange = (mode) => {
    setRow4ServiceMode(mode);
    setA3SuggestionDecision('');
    setA3PostSwitchNoteVisible(false);
    setPauseAutoShiftNotice('');
    setEnforcedNoBreakPauseHours(0);

    setAnswers((prev) => {
      if (mode === 'full-day') {
        return {
          ...prev,
          timeFrom: A3_DEFAULT_TIME_FROM,
          timeTo: A3_DEFAULT_TIME_TO,
          pauseAnswer: A3_PAUSE_DEFAULT,
        };
      }

      return {
        ...prev,
        timeFrom: prev.timeFrom || A3_DEFAULT_TIME_FROM,
        timeTo: prev.timeTo || A3_DEFAULT_TIME_TO,
        pauseAnswer: '',
      };
    });

    closeFormIfOpen();
  };

  const handleResponsibleChange = (value) => {
    setAnswers((prev) => ({
      ...prev,
      responsibleId: value,
    }));

    closeFormIfOpen();
    setA3PostSwitchNoteVisible(false);
  };

  const handleEntryVariantChange = (variant) => {
    setAnswers((prev) => {
      if (variant === 'a3') {
        return {
          ...prev,
          entryVariant: 'a3',
          timeFrom: A3_DEFAULT_TIME_FROM,
          timeTo: A3_DEFAULT_TIME_TO,
          pauseAnswer: A3_PAUSE_DEFAULT,
        };
      }

      if (variant === 'preset24h') {
        return {
          ...prev,
          entryVariant: 'preset24h',
          timeFrom: '09:00',
          timeTo: '12:30',
          pauseAnswer: '',
        };
      }

      return {
        ...prev,
        entryVariant: variant,
        pauseAnswer: getValidPauseAnswer(prev.entryType, variant, prev.pauseAnswer, configuredEntryTypeMetaByValue),
      };
    });

    closeFormIfOpen();
    setA3PostSwitchNoteVisible(false);
    setPauseAutoShiftNotice('');
    setEnforcedNoBreakPauseHours(0);
  };

  const handleTimeChange = (field, value) => {
    setAnswers((prev) => ({
      ...prev,
      [field]: value,
      pauseAnswer: isDayServiceWizardFlow
        ? prev.pauseAnswer
        : getValidPauseAnswer(prev.entryType, prev.entryVariant, prev.pauseAnswer, configuredEntryTypeMetaByValue),
    }));

    closeFormIfOpen();
    setA3SuggestionDecision('');
    setA3PostSwitchNoteVisible(false);
    setPauseAutoShiftNotice('');
    setEnforcedNoBreakPauseHours(0);
  };

  const handleTimeBlur = (field) => {
    setAnswers((prev) => ({
      ...prev,
      [field]: normalizeTimeValue(prev[field]),
    }));
  };

  const handleTimeWheel = (event, field, isFocused) => {
    if (!isFocused) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();

    const direction = event.deltaY < 0 ? 1 : -1;
    setAnswers((prev) => ({
      ...prev,
      [field]: shiftTimeByMinute(normalizeTimeValue(prev[field]) || '00:00', direction),
    }));

    closeFormIfOpen();
    setA3PostSwitchNoteVisible(false);
  };

  const handleHoursChange = (value, { markManual = true } = {}) => {
    const normalizedValue = normalizeDurationInput(value) || ZERO_DURATION;
    setAnswers((prev) => ({ ...prev, hours: normalizedValue }));
    if (markManual) {
      setIsQuestionFiveManuallyEdited(true);
      setQuestionFiveManualReference(suggestedQuestionFiveHours);
    }
    closeFormIfOpen();
    setA3PostSwitchNoteVisible(false);
  };

  const handleQuestionFivePartChange = (part, rawValue) => {
    const nextHoursPart = part === 'hours'
      ? normalizeQuestionFiveHourPart(rawValue)
      : String(Number(displayedQuestionFiveParts.hours || '0'));
    const nextMinutesPart = part === 'minutes'
      ? normalizeQuestionFiveMinutePart(rawValue)
      : displayedQuestionFiveParts.minutes;

    handleHoursChange(buildQuestionFiveHoursValue(nextHoursPart, nextMinutesPart));
  };

  const handleKeywordAdd = (value) => {
    const token = normalizeKeywordToken(value);
    if (!token) {
      return;
    }

    setAnswers((prev) => ({
      ...prev,
      keywords: normalizeKeywordList([...(prev.keywords || []), token]),
    }));
    setKeywordInput('');
  };

  const handleKeywordRemove = (value) => {
    const needle = String(value || '').toLocaleLowerCase('de');
    setAnswers((prev) => ({
      ...prev,
      keywords: normalizeKeywordList((prev.keywords || []).filter((keyword) => String(keyword || '').toLocaleLowerCase('de') !== needle)),
    }));
  };

  const handleKeywordInputKeyDown = (event) => {
    if (event.key !== 'Enter') {
      return;
    }

    event.preventDefault();
    handleKeywordAdd(keywordInput);
  };

  const handlePauseAnswerChange = (pauseAnswer) => {
    setA3SuggestionDecision('');
    const previousCompensationMinutes = Math.round(Number(enforcedNoBreakPauseHours || 0) * 60);
    const baseTimeTo = answers.pauseAnswer === 'nein' && previousCompensationMinutes > 0
      ? addMinutesToTime(answers.timeTo, -previousCompensationMinutes)
      : answers.timeTo;

    if (pauseAnswer === 'ja') {
      setAnswers((prev) => ({
        ...prev,
        pauseAnswer: 'ja',
        timeTo: baseTimeTo,
      }));
      setPauseAutoShiftNotice('');
      setEnforcedNoBreakPauseHours(0);
      closeFormIfOpen();
      setA3PostSwitchNoteVisible(false);
      return;
    }

    const currentDuration = calculateRawServiceHours(answers.timeFrom, baseTimeTo);
    const compensationPauseHours = getNoBreakCompensationPauseHours(currentDuration);
    const compensationMinutes = Math.round(compensationPauseHours * 60);
    const shouldShiftEnd = compensationMinutes > 0;
    const shiftedTimeTo = shouldShiftEnd ? addMinutesToTime(baseTimeTo, compensationMinutes) : baseTimeTo;

    setAnswers((prev) => ({
      ...prev,
      pauseAnswer: 'nein',
      timeTo: shiftedTimeTo,
    }));

    setEnforcedNoBreakPauseHours(compensationPauseHours);

    if (shouldShiftEnd && shiftedTimeTo) {
      const pauseLabel = formatHours(compensationPauseHours).replace('.', ',');
      setPauseAutoShiftNotice(
        `Hinweis: Das Ende der Veranstaltung wurde automatisch auf ${shiftedTimeTo} Uhr angepasst (+${pauseLabel} Pflichtpause).`,
      );
    } else if (compensationPauseHours === 0) {
      setPauseAutoShiftNotice('Hinweis: Bei einem Dienstzeitraum bis 6,00 Stunden ist keine Pflichtpause erforderlich.');
    }

    closeFormIfOpen();
    setA3PostSwitchNoteVisible(false);
  };

  const handleIncidentAnswerChange = (value) => {
    setIncidentAnswer(value);

    if (value === 'nein') {
      setIncidentFormState(getInitialIncidentFormState());
      setIncidentAttachmentFiles([]);
    }

    closeFormIfOpen();
  };

  const handleIncidentFieldChange = (fieldName, value) => {
    setIncidentFormState((prev) => ({ ...prev, [fieldName]: value }));
    closeFormIfOpen();
  };

  const handlePdfProtocolAnswerChange = (pdfProtocolAnswer) => {
    setAnswers((prev) => ({ ...prev, pdfProtocolAnswer }));
    closeFormIfOpen();
  };

  const handleDayServiceCodeGroupChange = (group) => {
    const nextVariant = resolveDayServiceVariantForGroup(group, adjustedServiceDurationForCode);
    if (!nextVariant) {
      return;
    }

    setAnswers((prev) => {
      return {
        ...prev,
        entryVariant: nextVariant,
      };
    });
    closeFormIfOpen();
  };

  const handleA3SuggestionDecision = (decision) => {
    setA3SuggestionDecision(decision);
    if (decision === 'ja') {
      handleAdoptA3Suggestion();
      return;
    }

    closeFormIfOpen();
  };

  const handleAdoptA3Suggestion = () => {
    setAnswers((prev) => {
      const nextTimeTo = addMinutesToTime(prev.timeFrom, FULL_DAY_TOTAL_MINUTES);
      if (!nextTimeTo) {
        return prev;
      }

      return {
        ...prev,
        timeTo: nextTimeTo,
        pauseAnswer: A3_PAUSE_DEFAULT,
      };
    });

    setA3PostSwitchNoteVisible(true);
    closeFormIfOpen();
    setPauseAutoShiftNotice('');
    setEnforcedNoBreakPauseHours(0);
  };

  const handleRow2Wheel = (event) => {
    if (!row2Focused) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();

    const optionValues = entryTypeOptions.map((option) => option.value);
    const nextValue = cycleWheelOption(answers.entryType, optionValues, event.deltaY);
    if (nextValue && nextValue !== answers.entryType) {
      handleEntryTypeChange(nextValue);
    }
  };

  const resetWizardFields = ({ clearMessages = true } = {}) => {
    setAnswers(getInitialWizardAnswers());
    setRow4ServiceMode('');
    setShowForm(false);
    setConfirmSaveOpen(false);
    setIncidentAnswer('');
    setIncidentFormState(getInitialIncidentFormState());
    setIncidentAttachmentFiles([]);
    setKeywordInput('');
    setKeywordSuggestions([]);
    if (clearMessages) {
      setSaveError('');
      setSaveWarning('');
      setSaveSuccess('');
      setSaveInjuryWarning(false);
    }
    setA3PostSwitchNoteVisible(false);
    setA3SuggestionDecision('');
    setPauseAutoShiftNotice('');
    setEnforcedNoBreakPauseHours(0);
    setIsQuestionFiveManuallyEdited(false);
    setQuestionFiveManualReference('');
  };

  const handleResetWizard = () => {
    resetWizardFields({ clearMessages: true });
  };

  const canOpenForm = Boolean(
    isRow1Complete
      && isRow2Complete
      && isRow3Complete
      && isRow4Complete
      && isRow4PauseComplete
      && isRow7A3RecommendationComplete
      && isRow5Complete
      && isKeywordRowComplete
      && isRow6Complete
      && isRow7Complete
      && isRow8Complete
      && isRow9Complete
  );

  const handleOpenConfirmSave = () => {
    if (!canOpenForm || saveLoading) {
      return;
    }

    if (!canCreateSelectedEntry) {
      setSaveWarning('');
      setSaveError('Mit dieser Rolle sind keine Logbucheintraege moeglich.');
      return;
    }

    setSaveError('');
    setSaveWarning('');
    setConfirmSaveOpen(true);
  };

  const handleConfirmSave = useCallback(async () => {
    setSaveLoading(true);
    setSaveError('');
    setSaveWarning('');
    setSaveInjuryWarning(false);

    try {
      const submitData = isSrhtBasicCourseWorkflow ? {
        date: selectedDate,
        responsibleId: answers.responsibleId ? Number.parseInt(answers.responsibleId, 10) : null,
        sourceEntryType: selectedEntryType,
        entryType: selectedEntryType,
        keywords: normalizeKeywordList(answers.keywords || []),
        userIds: (answers.copyUserIds || []).map((rawId) => Number(rawId)).filter((id) => Number.isFinite(id)),
      } : {
        date: selectedDate,
        hours: shouldForceZeroHours
          ? 0
          : durationStringToDecimalHours(normalizeDurationInput(answers.hours)),
        keywords: normalizeKeywordList(answers.keywords || []),
        description: '',
        responsibleId: selectedEntryRule.autoResponsibleCurrentUser
          ? Number(currentUserId)
          : (answers.responsibleId ? Number.parseInt(answers.responsibleId, 10) : null),
        sourceEntryType: selectedEntryType,
        entryType: getEffectiveEntryTypeForVariant(selectedEntryType, answers.entryVariant),
        entryVariant: answers.entryVariant || '',
        timeFrom: answers.timeFrom,
        timeTo: answers.timeTo,
      };

      if (!isSrhtBasicCourseWorkflow) {
        submitData.responsibleMode = isExternalResponsibleMode ? RESPONSIBLE_MODE_EXTERNAL : RESPONSIBLE_MODE_INTERNAL;

        if (isExternalResponsibleMode) {
          submitData.responsibleId = null;
          submitData.responsibleNameSnapshot = String(answers.responsibleNameSnapshot || '').trim();
          submitData.responsibleCompanySnapshot = String(answers.responsibleCompanySnapshot || '').trim();
          submitData.responsibleIsLehrhSnapshot = Boolean(answers.responsibleIsLehrhSnapshot);
        }
      }

      if (!isSrhtBasicCourseWorkflow && derivedPrepTime !== '' && !Number.isNaN(Number.parseFloat(derivedPrepTime))) {
        submitData.prepTime = Number.parseFloat(derivedPrepTime);
      }

      if (!isSrhtBasicCourseWorkflow && !usesPauseQuestion(selectedEntryType, answers.entryVariant, configuredEntryTypeMetaByValue)) {
        submitData.pauseTaken = false;
        submitData.pauseHours = 0;
      } else if (!isSrhtBasicCourseWorkflow && typeof answers.pauseAnswer === 'string' && answers.pauseAnswer) {
        if (answers.pauseAnswer === 'ja') {
          submitData.pauseTaken = true;
          submitData.pauseHours = getPauseHoursForDuration(baseServiceDuration, 'ja');
        } else {
          const forcedPauseHours = Number(enforcedNoBreakPauseHours || 0);
          submitData.pauseTaken = forcedPauseHours > 0;
          submitData.pauseHours = forcedPauseHours;
        }
      }

      if (!isSrhtBasicCourseWorkflow && Array.isArray(preparedUserEntries) && preparedUserEntries.length > 0) {
        submitData.userEntries = preparedUserEntries
          .map((entry) => ({
            userId: Number(entry.userId),
            timeFrom: entry.timeFrom || submitData.timeFrom,
            timeTo: entry.timeTo || submitData.timeTo,
            hours: shouldForceZeroHours
              ? 0
              : durationStringToDecimalHours(normalizeDurationInput(entry.hours)),
            entryVariant: entry.entryVariant || '',
            entryType: entry.entryType || submitData.entryType,
          }))
          .filter((entry) => Number.isFinite(entry.userId) && Number.isFinite(entry.hours));
      }

      const response = await client.post('/api/training/add', submitData);
      const warningMessages = [];
      const successMessage = response.data?.message || 'Eintragung erfolgreich gespeichert.';
      setSaveSuccess(successMessage);
      setSaveInjuryWarning(
        incidentAnswer === 'ja' && incidentFormState.injuryType === 'with_person_damage',
      );
      setConfirmSaveOpen(false);

      if (incidentAnswer === 'ja') {
        try {
          const incidentPayload = buildIncidentSubmitPayload(incidentFormState, incidentOptions);
          const incidentResponse = await createFeedbackEntry(incidentPayload);
          const incidentId = incidentResponse?.data?.id;
          const incidentFiles = [...incidentAttachmentFiles];

          if (incidentId && incidentFiles.length > 0) {
            await uploadFeedbackAssets(incidentId, incidentFiles);
          }
        } catch (incidentError) {
          const details = incidentError?.response?.data?.error || incidentError?.message || '';
          warningMessages.push(
            details
              ? `Der Zwischenfall konnte nicht gespeichert werden (${details}).`
              : 'Der Zwischenfall konnte nicht gespeichert werden.',
          );
        }
      }

      if (!isSrhtBasicCourseWorkflow && answers.pdfProtocolAnswer === 'ja') {
        const responsibleCandidate = sortedUsers.find(
          (candidate) => String(candidate.id) === String(answers.responsibleId),
        );
        const protocolResponsibleName = selectedEntryRule.autoResponsibleCurrentUser
          ? responsiblePersonLabel
          : (isExternalResponsibleMode ? (externalResponsibleDisplayName || '-') : (responsibleCandidate?.full_name || '-'));

        const inheritedValues = {
          timeFrom: answers.timeFrom,
          timeTo: answers.timeTo,
        };

        const ownEffectiveEntryType = getEffectiveEntryTypeForVariant(selectedEntryType, answers.entryVariant);
        const ownPauseHours = getPauseHoursForExport(
          selectedEntryType,
          answers.entryVariant,
          inheritedValues.timeFrom,
          inheritedValues.timeTo,
          answers.pauseAnswer,
          enforcedNoBreakPauseHours,
          configuredEntryTypeMetaByValue,
        );
        const ownServiceDuration = calculateDisplayedServiceHours(
          selectedEntryType,
          answers.entryVariant,
          inheritedValues.timeFrom,
          inheritedValues.timeTo,
          answers.pauseAnswer,
          enforcedNoBreakPauseHours,
          configuredEntryTypeMetaByValue,
        );
        const ownUnscaledWorkDuration = ownServiceDuration ?? adjustedServiceDuration;
        const ownScaledWorkDuration = scaleWorkHours(ownEffectiveEntryType, ownUnscaledWorkDuration, configuredEntryTypeMetaByValue);

        const protocolEntries = answers.copyToOthers === 'ja'
          ? preparedUserEntries
          : [{
            userId: currentUserId,
            timeFrom: inheritedValues.timeFrom,
            timeTo: inheritedValues.timeTo,
            entryVariant: answers.entryVariant || '',
            entryType: ownEffectiveEntryType,
          }];
        const participants = protocolEntries.map((protocolEntry) => {
          const candidate = eligibleCopyUsers.find((item) => Number(item.id) === Number(protocolEntry.userId));
          const serviceDuration = calculateDisplayedServiceHours(
            selectedEntryType,
            protocolEntry.entryVariant,
            protocolEntry.timeFrom,
            protocolEntry.timeTo,
            answers.pauseAnswer,
            enforcedNoBreakPauseHours,
            configuredEntryTypeMetaByValue,
          );
          const pauseHours = getPauseHoursForExport(
            selectedEntryType,
            protocolEntry.entryVariant,
            protocolEntry.timeFrom,
            protocolEntry.timeTo,
            answers.pauseAnswer,
            enforcedNoBreakPauseHours,
            configuredEntryTypeMetaByValue,
          );
          const scaledWorkDuration = scaleWorkHours(protocolEntry.entryType, serviceDuration, configuredEntryTypeMetaByValue);

          return {
            fullName: candidate?.full_name || `Unbekannter Benutzer (${protocolEntry.userId})`,
            wachbereitschaft: candidate?.wachbereitschaft || '',
            entryType: protocolEntry.entryType,
            entryVariant: protocolEntry.entryVariant,
            timeFrom: protocolEntry.timeFrom,
            timeTo: protocolEntry.timeTo,
            serviceDuration,
            unscaledWorkDuration: serviceDuration,
            scaledWorkDuration,
            pauseHours,
          };
        });

        const additionalLogbooks = answers.copyToOthers === 'ja'
          ? (answers.copyUserIds || []).filter((copyUserId) => Number(copyUserId) !== currentUserId).map((copyUserId) => {
            const key = String(copyUserId);
            const candidate = eligibleCopyUsers.find((item) => String(item.id) === key);
            const label = candidate?.full_name || `Unbekannter Benutzer (${copyUserId})`;
            const overrideEntry = answers.copyUserOverrides?.[key];
            if (!overrideEntry?.override) {
              return label;
            }

            const timeFromLabel = overrideEntry.timeFrom || inheritedValues.timeFrom || '-';
            const timeToLabel = overrideEntry.timeTo || inheritedValues.timeTo || '-';
            const variantLabel = getEntryVariantLabel(selectedEntryType, overrideEntry.entryVariant || answers.entryVariant || '');
            return `${label} (${variantLabel ? `${variantLabel}, ` : ''}${timeFromLabel} bis ${timeToLabel})`;
          }).join(', ') || 'Nein'
          : 'Nein';

        try {
          const affectedUserIds = protocolEntries
            .map((entry) => Number(entry.userId))
            .filter((id) => Number.isFinite(id));

          const exportResult = await downloadDienstzeitProtokollPdf({
            entryIds: response.data?.ids || [],
            protocolPayload: {
              date: selectedDate,
              entryType: selectedEntryType,
              responsibleName: protocolResponsibleName,
              timeFrom: inheritedValues.timeFrom,
              timeTo: inheritedValues.timeTo,
              serviceDuration: ownUnscaledWorkDuration,
              unscaledWorkDuration: ownUnscaledWorkDuration,
              scaledWorkDuration: ownScaledWorkDuration,
              pauseHours: ownPauseHours,
              additionalLogbooks,
              affectedUserIds: [...new Set(affectedUserIds)],
              participants,
            },
          });
          if (exportResult?.exportId) {
            // no-op
          }
        } catch (pdfError) {
          const details = pdfError?.message ? ` (${pdfError.message})` : '';
          warningMessages.push(`Der automatische PDF-Download ist fehlgeschlagen.${details}`);
        }
      }

      if (warningMessages.length > 0) {
        setSaveWarning(`Eintragung wurde gespeichert. ${warningMessages.join(' ')}`);
        setSaveError('');
      } else {
        setSaveWarning('');
      }

      resetWizardFields({ clearMessages: false });
    } catch (error) {
      setSaveWarning('');
      setSaveError(error.response?.data?.error || 'Fehler beim Speichern der Eintragung.');
    } finally {
      setSaveLoading(false);
    }
  }, [
    answers.entryVariant,
    answers.hours,
    answers.pauseAnswer,
    answers.pdfProtocolAnswer,
    answers.copyToOthers,
    answers.copyUserIds,
    answers.copyUserOverrides,
    answers.keywords,
    answers.responsibleCompanySnapshot,
    answers.responsibleId,
    answers.responsibleIsLehrhSnapshot,
    answers.responsibleNameSnapshot,
    answers.timeFrom,
    answers.timeTo,
    adjustedServiceDuration,
    derivedPrepTime,
    baseServiceDuration,
    enforcedNoBreakPauseHours,
    eligibleCopyUsers,
    isSrhtBasicCourseWorkflow,
    preparedUserEntries,
    currentUserId,
    responsiblePersonLabel,
    shouldForceZeroHours,
    selectedEntryRule.autoResponsibleCurrentUser,
    selectedDate,
    selectedEntryType,
    incidentAnswer,
    incidentFormState,
    incidentOptions,
    incidentAttachmentFiles,
    configuredEntryTypeMetaByValue,
    externalResponsibleDisplayName,
    sortedUsers,
    isExternalResponsibleMode,
  ]);

  const responsibleName = useMemo(() => {
    if (selectedEntryRule.autoResponsibleCurrentUser) {
      return responsiblePersonLabel;
    }

    if (isExternalResponsibleMode) {
      return externalResponsibleDisplayName || '-';
    }

    return selectedResponsibleCandidate?.full_name || '-';
  }, [selectedEntryRule.autoResponsibleCurrentUser, responsiblePersonLabel, selectedResponsibleCandidate, isExternalResponsibleMode, externalResponsibleDisplayName]);

  const copyUsersSummary = useMemo(() => {
    if (answers.copyToOthers !== 'ja') {
      return [];
    }

    const inheritedValues = getInheritedCopyValues();

    return (answers.copyUserIds || []).map((userId) => {
      const key = String(userId);
      const candidate = eligibleCopyUsers.find((item) => String(item.id) === key);
      if (isSrhtBasicCourseWorkflow) {
        return {
          label: candidate?.full_name || `Unbekannter Benutzer (${userId})`,
          hasOverride: false,
          variantLabel: '',
          timeLabel: '',
        };
      }

      const overrideEntry = answers.copyUserOverrides?.[key];
      const hasOverride = Boolean(overrideEntry?.override);
      const fromValue = hasOverride ? (overrideEntry?.timeFrom || inheritedValues.timeFrom) : inheritedValues.timeFrom;
      const toValue = hasOverride ? (overrideEntry?.timeTo || inheritedValues.timeTo) : inheritedValues.timeTo;
      const variantValue = hasOverride ? (overrideEntry?.entryVariant || inheritedValues.entryVariant) : inheritedValues.entryVariant;

      return {
        label: candidate?.full_name || `Unbekannter Benutzer (${userId})`,
        hasOverride,
        variantLabel: getEntryVariantLabel(selectedEntryType, variantValue),
        timeLabel: `${normalizeTimeValue(fromValue) || '-'} bis ${normalizeTimeValue(toValue) || '-'}`,
      };
    });
  }, [
    answers.copyToOthers,
    answers.copyUserIds,
    answers.copyUserOverrides,
    eligibleCopyUsers,
    getInheritedCopyValues,
    isSrhtBasicCourseWorkflow,
    selectedEntryType,
  ]);

  return (
    <Box sx={{ px: { xs: 1.5, sm: 2, md: 5 }, py: { xs: 2, md: 2.5 } }}>
      <Stack spacing={2.5}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.75, fontSize: { xs: '1.65rem', sm: '2rem' } }}>
            Logbucheintrag erstellen
          </Typography>
        </Box>

        <Card sx={{ borderRadius: 3, border: 1, borderColor: 'divider' }}>
          <CardContent sx={{ p: { xs: 2, md: 3 } }}>
            <Stack spacing={2}>
              {saveError && <Alert severity="error">{saveError}</Alert>}
              {saveWarning && (
                <Alert severity="warning">
                  <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                    {saveWarning}
                  </Typography>
                  <Typography variant="body2">
                    Du kannst das Stundenprotokoll jederzeit über den Bereich "Benachrichtigungen" erneut herunterladen.
                  </Typography>
                </Alert>
              )}
              {saveSuccess && <Alert severity="success">{saveSuccess}</Alert>}
              {saveInjuryWarning && (
                <Alert severity="error" variant="filled">
                  Informiere bitte unverzüglich die Leitung der Höhenrettung! Es ist zu einem Personenschaden gekommen.
                </Alert>
              )}

              <Typography variant="body2" color="text.secondary">
                {dateStep.question}
              </Typography>

              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25}>
                {dateStep.options.map((option) => {
                  const isSelected = answers.dateChoice === option.value;
                  return (
                    <Button
                      key={option.value}
                      variant={isSelected ? 'contained' : 'outlined'}
                      onClick={() => handleDateChoiceChange(option.value)}
                      sx={{ minHeight: 44 }}
                    >
                      {option.label}
                    </Button>
                  );
                })}
              </Stack>

              {answers.dateChoice === 'custom' && (
                <Stack spacing={1.25}>
                  <DateInputField
                    fullWidth
                    size="small"
                    label={dateStep.dateLabel}
                    value={answers.date || ''}
                    onChange={handleCustomDateChange}
                    helperText={isMobile
                      ? 'Datum direkt eingeben, z. B. 14042026.'
                      : 'Datum direkt eingeben, z. B. 14042026 oder 14.04.2026.'}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                        backgroundColor: 'background.paper',
                        minHeight: isMobile ? 44 : undefined,
                      },
                    }}
                  />
                </Stack>
              )}

              {isRow1Complete && (
                <>
                  <Typography variant="body2" color="text.secondary">
                    {entryTypeStep.question}
                  </Typography>

                  <FormControl fullWidth onWheel={handleRow2Wheel}>
                    <InputLabel id="wizard-entry-type-label">{entryTypeStep.label}</InputLabel>
                    <Select
                      labelId="wizard-entry-type-label"
                      value={answers.entryType || ''}
                      label={entryTypeStep.label}
                      onFocus={() => setRow2Focused(true)}
                      onBlur={() => setRow2Focused(false)}
                      onChange={(event) => handleEntryTypeChange(event.target.value)}
                    >
                      <MenuItem value="">
                        <em>Bitte auswählen</em>
                      </MenuItem>
                      {entryTypeOptions.map((option) => (
                        <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </>
              )}

              {isRow2Complete && !selectedEntryRule.autoResponsibleCurrentUser && (
                <>
                  <Typography variant="body2" color="text.secondary">
                    {responsibleQuestion}
                  </Typography>

                  {supportsExternalResponsible && (
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25}>
                      <Button
                        variant={!isExternalResponsibleMode ? 'contained' : 'outlined'}
                        onClick={() => handleResponsibleModeChange(RESPONSIBLE_MODE_INTERNAL)}
                      >
                        Mitglied SRHT DUS
                      </Button>
                      <Button
                        variant={isExternalResponsibleMode ? 'contained' : 'outlined'}
                        onClick={() => handleResponsibleModeChange(RESPONSIBLE_MODE_EXTERNAL)}
                      >
                        Externer Dozent erfassen
                      </Button>
                    </Stack>
                  )}

                  {!isExternalResponsibleMode ? (
                    <>
                      {usersError && <Alert severity="warning">{usersError}</Alert>}

                      <Autocomplete
                        id="wizard-responsible"
                        fullWidth
                        disabled={usersLoading}
                        options={sortedUsers}
                        value={selectedResponsibleCandidate}
                        loading={usersLoading}
                        filterOptions={(options, state) => filterResponsibleCandidates(options, state.inputValue)}
                        getOptionLabel={getResponsibleCandidateLabel}
                        isOptionEqualToValue={(option, value) => String(option?.id) === String(value?.id)}
                        onChange={(event, option) => handleResponsibleChange(option ? String(option.id) : '')}
                        openOnFocus
                        autoHighlight
                        forcePopupIcon
                        clearOnBlur={false}
                        loadingText="Wird geladen..."
                        noOptionsText="Keine passenden Personen gefunden"
                        openText="Liste öffnen"
                        closeText="Liste schließen"
                        clearText="Auswahl löschen"
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Verantwortliche Person"
                            placeholder="Name suchen"
                          />
                        )}
                      />
                    </>
                  ) : (
                    <Stack spacing={1.25}>
                      <TextField
                        fullWidth
                        label="Name des Verantwortlichen"
                        placeholder="Nachname, Vorname"
                        value={answers.responsibleNameSnapshot || ''}
                        onChange={(event) => setAnswers((prev) => ({
                          ...prev,
                          responsibleNameSnapshot: event.target.value,
                        }))}
                      />
                      <TextField
                        fullWidth
                        label="Firma / Organisation"
                        placeholder="z. B. Institut der Feuerwehr NRW"
                        value={answers.responsibleCompanySnapshot || ''}
                        onChange={(event) => setAnswers((prev) => ({
                          ...prev,
                          responsibleCompanySnapshot: event.target.value,
                        }))}
                      />
                      <Box>
                        <FormControlLabel
                          control={(
                            <Checkbox
                              checked={Boolean(answers.responsibleIsLehrhSnapshot)}
                              onChange={(event) => setAnswers((prev) => ({
                                ...prev,
                                responsibleIsLehrhSnapshot: event.target.checked,
                              }))}
                            />
                          )}
                          label="Lehrhöhenretterstunden"
                        />
                        <Typography variant="caption" color="text.secondary" display="block">
                          Wenn aktiviert, werden die Stunden zusätzlich als Lehrhöhenretter-Stunden gewertet.
                        </Typography>
                      </Box>
                    </Stack>
                  )}
                </>
              )}

              {isRow3Complete && row4Mode !== 'srht-basic-course' && (
                <>
                  {isDayServiceWizardFlow ? (
                    <Stack spacing={1.25}>
                      <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mt: 0.5 }}>
                        <Typography variant="body2" color="text.secondary">
                          4. Soll ein vollständiger Tagesdienst oder ein individueller Zeitraum erfasst werden?
                        </Typography>
                        <Tooltip
                          title="Vollständiger Tagesdienst setzt den Zeitraum auf 07:00 bis 15:42 Uhr (8:12h Dienstzeit + 30min Pause). Bei individuellem Zeitraum gelten die Pausenregeln: bis 6:00h keine Pause, über 6:00h bis 9:00h 30min, über 9:00h 45min."
                          placement={isMobile ? 'bottom' : 'right'}
                        >
                          <IconButton size="small" aria-label="Info zu Zeile 4 Tagesdienst">
                            <InfoOutlinedIcon fontSize="inherit" />
                          </IconButton>
                        </Tooltip>
                      </Stack>

                      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25}>
                        <Button
                          variant={row4ServiceMode === 'full-day' ? 'contained' : 'outlined'}
                          onClick={() => handleRow4ServiceModeChange('full-day')}
                        >
                          Vollständiger Tagesdienst
                        </Button>
                        <Button
                          variant={row4ServiceMode === 'individual' ? 'contained' : 'outlined'}
                          onClick={() => handleRow4ServiceModeChange('individual')}
                        >
                          Individueller Zeitraum
                        </Button>
                      </Stack>

                      {row4ServiceMode === 'full-day' && (
                        <Alert severity="info">
                          Zeitraum fest gesetzt: 07:00 bis 15:42 Uhr. Pause ist automatisch auf "Ja" gesetzt.
                        </Alert>
                      )}

                      {row4ServiceMode === 'individual' && (
                        <>
                          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25}>
                            <TextField
                              fullWidth
                              type="time"
                              label="Von"
                              value={answers.timeFrom || ''}
                              onFocus={() => setRow4FromFocused(true)}
                              onBlur={() => {
                                setRow4FromFocused(false);
                                handleTimeBlur('timeFrom');
                              }}
                              onWheelCapture={(event) => handleTimeWheel(event, 'timeFrom', row4FromFocused)}
                              onInput={(event) => handleTimeChange('timeFrom', event.target.value)}
                              onChange={(event) => handleTimeChange('timeFrom', event.target.value)}
                              inputProps={{ step: 60 }}
                              InputLabelProps={{ shrink: true }}
                            />
                            <TextField
                              fullWidth
                              type="time"
                              label="Bis"
                              value={answers.timeTo || ''}
                              onFocus={() => setRow4ToFocused(true)}
                              onBlur={() => {
                                setRow4ToFocused(false);
                                handleTimeBlur('timeTo');
                              }}
                              onWheelCapture={(event) => handleTimeWheel(event, 'timeTo', row4ToFocused)}
                              onInput={(event) => handleTimeChange('timeTo', event.target.value)}
                              onChange={(event) => handleTimeChange('timeTo', event.target.value)}
                              inputProps={{ step: 60 }}
                              InputLabelProps={{ shrink: true }}
                            />
                          </Stack>

                          <Typography variant="body2" color="text.secondary">
                            4b. Wurden Pausen gemacht?
                          </Typography>

                          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25}>
                            <Button
                              variant={answers.pauseAnswer === 'ja' ? 'contained' : 'outlined'}
                              onClick={() => handlePauseAnswerChange('ja')}
                              sx={{ minHeight: 44 }}
                            >
                              Ja
                            </Button>
                            <Button
                              variant={answers.pauseAnswer === 'nein' ? 'contained' : 'outlined'}
                              onClick={() => handlePauseAnswerChange('nein')}
                              sx={{ minHeight: 44 }}
                            >
                              Nein
                            </Button>
                          </Stack>
                        </>
                      )}

                      {pauseAutoShiftNotice && (
                        <Alert severity="info">
                          {pauseAutoShiftNotice}
                        </Alert>
                      )}

                      {a3PostSwitchNoteVisible && (
                        <Alert severity="success">
                          Vermerk: Zeitraum wurde auf vollständigen Tagesdienst angepasst (Startzeit beibehalten, Ende auf +8h42, Pause auf "Ja").
                        </Alert>
                      )}

                      {isRow4Complete && isRow4PauseComplete && adjustedServiceDuration !== null && (
                        <Typography variant="body2" color="text.secondary">
                          {serviceDurationLabel}: {(decimalHoursToDurationString(adjustedServiceDuration) || ZERO_DURATION)}h
                        </Typography>
                      )}
                    </Stack>
                  ) : (
                    <Stack spacing={1.25}>
                      <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mt: 0.5 }}>
                        <Typography variant="body2" color="text.secondary">
                          {timeQuestion}
                        </Typography>
                        {isA3VariantEntry && (
                          <Tooltip
                            title="A3 = 07:00-15:42 inkl. 30min Pause (8h12 Arbeitszeit) / zA3 = Freie Dienstzeiteintragung moeglich"
                            placement={isMobile ? 'bottom' : 'right'}
                          >
                            <IconButton size="small" aria-label="Info zu Zeile 4">
                              <InfoOutlinedIcon fontSize="inherit" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Stack>

                      {row4Mode === 'preset-or-custom' && (
                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25}>
                          <Button
                            variant={answers.entryVariant === 'preset24h' ? 'contained' : 'outlined'}
                            onClick={() => handleEntryVariantChange('preset24h')}
                          >
                            09:00 Uhr bis 12:30 Uhr
                          </Button>
                          <Button
                            variant={answers.entryVariant === 'custom24h' ? 'contained' : 'outlined'}
                            onClick={() => handleEntryVariantChange('custom24h')}
                          >
                            Individueller Zeitraum
                          </Button>
                        </Stack>
                      )}

                      {row4Mode === 'a3-za3' && !shouldUseDayServiceCodeStep && (
                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25}>
                          <Button
                            variant={answers.entryVariant === 'a3' ? 'contained' : 'outlined'}
                            onClick={() => handleEntryVariantChange('a3')}
                          >
                            A3-Eintragung
                          </Button>
                          <Button
                            variant={answers.entryVariant === 'za3' ? 'contained' : 'outlined'}
                            onClick={() => handleEntryVariantChange('za3')}
                          >
                            zA3-Eintragung
                          </Button>
                        </Stack>
                      )}

                      {(isCustomPeriodVariant(selectedEntryType, answers.entryVariant) || row4Mode === 'custom-only') && (
                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25}>
                          <TextField
                            fullWidth
                            type="time"
                            label="Von"
                            value={answers.timeFrom || ''}
                            onFocus={() => setRow4FromFocused(true)}
                            onBlur={() => {
                              setRow4FromFocused(false);
                              handleTimeBlur('timeFrom');
                            }}
                            onWheelCapture={(event) => handleTimeWheel(event, 'timeFrom', row4FromFocused)}
                            onInput={(event) => handleTimeChange('timeFrom', event.target.value)}
                            onChange={(event) => handleTimeChange('timeFrom', event.target.value)}
                            inputProps={{ step: 60 }}
                            InputLabelProps={{ shrink: true }}
                          />
                          <TextField
                            fullWidth
                            type="time"
                            label="Bis"
                            value={answers.timeTo || ''}
                            onFocus={() => setRow4ToFocused(true)}
                            onBlur={() => {
                              setRow4ToFocused(false);
                              handleTimeBlur('timeTo');
                            }}
                            onWheelCapture={(event) => handleTimeWheel(event, 'timeTo', row4ToFocused)}
                            onInput={(event) => handleTimeChange('timeTo', event.target.value)}
                            onChange={(event) => handleTimeChange('timeTo', event.target.value)}
                            inputProps={{ step: 60 }}
                            InputLabelProps={{ shrink: true }}
                          />
                        </Stack>
                      )}

                      {isRow4Complete && adjustedServiceDuration !== null && (
                        <Typography variant="body2" color="text.secondary">
                          {serviceDurationLabel}: {(decimalHoursToDurationString(adjustedServiceDuration) || ZERO_DURATION)}h
                        </Typography>
                      )}
                    </Stack>
                  )}
                </>
              )}

              {shouldShowRow7A3Recommendation && (
                <>
                  <Typography variant="body2" color="text.secondary">
                    4c. Soll der Eintrag auf einen vollständigen Tagesdienst umgestellt werden?
                  </Typography>

                  <Alert severity="info">
                    Der individuelle Zeitraum liegt nahe an einem vollständigen Tagesdienst.
                  </Alert>

                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25}>
                    <Button
                      variant={a3SuggestionDecision === 'ja' ? 'contained' : 'outlined'}
                      onClick={() => handleA3SuggestionDecision('ja')}
                      sx={{ minHeight: 44 }}
                    >
                      Ja
                    </Button>
                    <Button
                      variant={a3SuggestionDecision === 'nein' ? 'contained' : 'outlined'}
                      onClick={() => handleA3SuggestionDecision('nein')}
                      sx={{ minHeight: 44 }}
                    >
                      Nein
                    </Button>
                  </Stack>
                </>
              )}

              {isRow4Complete && isRow4PauseComplete && isRow7A3RecommendationComplete && !shouldForceZeroHours && row4Mode !== 'srht-basic-course' && (
                <>
                  <Typography variant="body2" color="text.secondary">
                    5. Wie viele Höhenretterstunden wurden absolviert?
                  </Typography>

                  <Card variant="outlined" sx={{ borderRadius: 2, p: 1.5 }}>
                    <Stack spacing={1.25}>
                      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25}>
                        <TextField
                          id="question-five-hours"
                          fullWidth
                          label="Stunden"
                          value={String(Number(displayedQuestionFiveParts.hours || '0'))}
                          onChange={(event) => handleQuestionFivePartChange('hours', event.target.value)}
                          inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
                        />
                        <TextField
                          id="question-five-minutes"
                          fullWidth
                          label="Minuten"
                          value={displayedQuestionFiveParts.minutes}
                          onChange={(event) => handleQuestionFivePartChange('minutes', event.target.value)}
                          inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
                        />
                      </Stack>

                      <Stack spacing={0.35}>
                        <Typography variant="body2" color="text.secondary">
                          Volle anrechenbare Dienstzeit: {suggestedQuestionFiveHours}h
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Eingetragene Höhenretterstunden: {displayedQuestionFiveHours}h
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Vor-/Nachbereitung: {derivedPrepTime === '' ? '-' : `${decimalHoursToDurationString(derivedPrepTime) || ZERO_DURATION}h`}
                        </Typography>
                      </Stack>

                      {showQuestionFiveSyncNotice && (
                        <Alert severity="info">
                          Zeitraum oder Pausen wurden geändert. Deine manuelle Eingabe bleibt erhalten; prüfe die Höhenretterstunden bitte kurz.
                        </Alert>
                      )}
                    </Stack>
                  </Card>
                </>
              )}

              {isRow4Complete && isRow4PauseComplete && isRow7A3RecommendationComplete && isRow5Complete && shouldShowKeywordRow && (
                <>
                  <Typography variant="body2" color="text.secondary">
                    5a. Welche Schlagwörter beschreiben den Inhalt der Veranstaltung?
                    {isKeywordRequired ? ' (Pflichtfeld)' : ''}
                  </Typography>

                  <Card variant="outlined" sx={{ borderRadius: 2, p: 1.25 }}>
                    <Stack spacing={1}>
                      <TextField
                        size="small"
                        fullWidth
                        label="Schlagwort eingeben und Enter drücken"
                        value={keywordInput}
                        onChange={(event) => setKeywordInput(event.target.value)}
                        onKeyDown={handleKeywordInputKeyDown}
                        helperText={`${(answers.keywords || []).length}/${KEYWORD_MAX_PER_ENTRY} Schlagwörter`}
                      />

                      {!!keywordSuggestions.length && (
                        <Stack direction="row" spacing={0.7} flexWrap="wrap" useFlexGap>
                          {keywordSuggestions.slice(0, 8).map((suggestion) => (
                            <Chip
                              key={`keyword-suggestion-${suggestion}`}
                              size="small"
                              variant="outlined"
                              label={suggestion}
                              onClick={() => handleKeywordAdd(suggestion)}
                            />
                          ))}
                        </Stack>
                      )}

                      <Stack direction="row" spacing={0.7} flexWrap="wrap" useFlexGap>
                        {(answers.keywords || []).map((keyword) => (
                          <Chip
                            key={`keyword-selected-${keyword}`}
                            size="small"
                            color="primary"
                            label={keyword}
                            onDelete={() => handleKeywordRemove(keyword)}
                          />
                        ))}
                      </Stack>

                      {isKeywordRequired && !(answers.keywords || []).length && (
                        <Typography variant="caption" color="warning.main">
                          Bitte mindestens ein Schlagwort hinterlegen.
                        </Typography>
                      )}
                    </Stack>
                  </Card>
                </>
              )}

              {shouldShowRow6 && (
                <>
                  <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mt: 0.5 }}>
                    <Typography variant="body2" color="text.secondary">
                      6. Wurden Pausen gemacht?
                    </Typography>
                    <Tooltip
                      title="Trage hier die tatsächliche Uhrzeit ein und beantworte die Frage ehrlich. Bei Pause = Nein wird das Ende automatisch korrigiert: bis 6,00h keine Pause, über 6,00h bis 9,00h +30min, über 9,00h +45min."
                      placement={isMobile ? 'bottom' : 'right'}
                    >
                      <IconButton size="small" aria-label="Info zu Zeile 6">
                        <InfoOutlinedIcon fontSize="inherit" />
                      </IconButton>
                    </Tooltip>
                  </Stack>

                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25}>
                    <Button
                      variant={answers.pauseAnswer === 'ja' ? 'contained' : 'outlined'}
                      onClick={() => handlePauseAnswerChange('ja')}
                      sx={{ minHeight: 44 }}
                    >
                      Ja
                    </Button>
                    <Button
                      variant={answers.pauseAnswer === 'nein' ? 'contained' : 'outlined'}
                      onClick={() => handlePauseAnswerChange('nein')}
                      sx={{ minHeight: 44 }}
                    >
                      Nein
                    </Button>
                  </Stack>

                  {pauseAutoShiftNotice && (
                    <Alert severity="info">
                      {pauseAutoShiftNotice}
                    </Alert>
                  )}

                  {a3PostSwitchNoteVisible && (
                    <Alert severity="success">
                      Vermerk: Nachtraeglich auf A3 gewechselt. Startzeit beibehalten, Endzeit auf +8h42 gesetzt, Pause auf "Ja" gesetzt.
                    </Alert>
                  )}
                </>
              )}

              {shouldShowRow7Code && (
                <>
                  <Typography variant="body2" color="text.secondary">
                    {codeQuestionNumber}. Welches Dienstkürzel soll genutzt werden?
                  </Typography>

                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25}>
                    {supportsA3Za3Selection && (
                      <Button
                        variant={selectedDayServiceCodeGroup === 'A' ? 'contained' : 'outlined'}
                        onClick={() => handleDayServiceCodeGroupChange('A')}
                      >
                        A3/zA3
                      </Button>
                    )}
                    {supportsTGroupSelection && (
                      <Button
                        variant={selectedDayServiceCodeGroup === 'T' ? 'contained' : 'outlined'}
                        onClick={() => handleDayServiceCodeGroupChange('T')}
                      >
                        TE1/2
                      </Button>
                    )}
                    {supportsHonorarSelection && (
                      <Button
                        variant={selectedDayServiceCodeGroup === 'HONORAR' ? 'contained' : 'outlined'}
                        onClick={() => handleDayServiceCodeGroupChange('HONORAR')}
                      >
                        Honorar
                      </Button>
                    )}
                  </Stack>

                  {selectedDayServiceCodeGroup && adjustedServiceDuration !== null && (
                    <Alert severity="info">
                      Automatische Zuordnung: {selectedDayServiceCodeGroup === 'T'
                        ? (adjustedServiceDuration <= 6 ? 'TE2' : 'TE1')
                        : (selectedDayServiceCodeGroup === 'A'
                          ? (isExactA3ServiceDuration(adjustedServiceDuration) ? 'A3' : 'zA3')
                          : 'Honorar')}.
                    </Alert>
                  )}
                </>
              )}

              {shouldShowRow7 && (
                <>
                  <Typography variant="body2" color="text.secondary">
                    {isSrhtBasicCourseWorkflow
                      ? `${copyQuestionNumber}. Welche Bewerber sollen als Teilnehmer des SRHT Grundlehrganges eingetragen werden?`
                      : `${copyQuestionNumber}. Soll der Eintrag in andere Logbücher übernommen werden?`}
                  </Typography>

                  {!isSrhtBasicCourseWorkflow && (
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25}>
                      <Button
                        variant={answers.copyToOthers === 'ja' ? 'contained' : 'outlined'}
                        onClick={() => handleCopyDecisionChange('ja')}
                      >
                        Ja
                      </Button>
                      <Button
                        variant={answers.copyToOthers === 'nein' ? 'contained' : 'outlined'}
                        onClick={() => handleCopyDecisionChange('nein')}
                      >
                        Nein
                      </Button>
                    </Stack>
                  )}

                  {(isSrhtBasicCourseWorkflow || answers.copyToOthers === 'ja') && (
                    <Card variant="outlined" sx={{ borderRadius: 2, p: 1.5 }}>
                      <Stack spacing={1.5}>
                        {isSrhtBasicCourseWorkflow && eligibleCopyUsers.length === 0 && (
                          <Alert severity="warning">
                            Es stehen aktuell keine Nutzer mit ausschliesslich der Rolle Bewerber SRHT zur Auswahl.
                          </Alert>
                        )}

                        <Stack spacing={1}>
                          {eligibleCopyUsers.map((candidate) => {
                            const idString = String(candidate.id);
                            const selected = (answers.copyUserIds || []).map(String).includes(idString);
                            const isCurrentCandidate = Number(candidate.id) === currentUserId;
                            const ownSelectionLocked = isCurrentCandidate && !allowOwnLogbookDeselection;

                            if (isSrhtBasicCourseWorkflow) {
                              return (
                                <Card key={candidate.id} variant="outlined" sx={{ p: 1.25, borderRadius: 2 }}>
                                  <Button
                                    fullWidth
                                    variant={selected ? 'contained' : 'outlined'}
                                    onClick={() => handleCopyUserToggle(candidate.id)}
                                    sx={{ textTransform: 'none', justifyContent: 'flex-start' }}
                                  >
                                    {candidate.full_name}
                                  </Button>
                                </Card>
                              );
                            }

                            const overrideEntry = answers.copyUserOverrides?.[idString];
                            const overrideEnabled = Boolean(overrideEntry?.override);
                            const isExpanded = Boolean(overrideEntry?.expanded);
                            const showTimeInputs = Boolean(overrideEntry?.showTimeInputs);
                            const effectiveVariant = overrideEnabled
                              ? (overrideEntry?.entryVariant || answers.entryVariant || '')
                              : (answers.entryVariant || '');
                            const effectiveBaseVariant = getDayEntryBaseVariant(effectiveVariant);
                            const isFlexEnabled = isDayEntryFlexEnabled(effectiveVariant);
                            const isDozentEntryType = isDozentDayEntryType(selectedEntryType);
                            const shouldShowCopyTimeInputs = shouldUseDayServiceCodeStep
                              ? overrideEnabled
                              : (isDozentEntryType
                                ? (isFlexEnabled && (showTimeInputs || !overrideEnabled))
                                : overrideEnabled);
                            const inheritedValues = getInheritedCopyValues();
                            const displayedValues = overrideEnabled
                              ? {
                                timeFrom: overrideEntry?.timeFrom ?? inheritedValues.timeFrom,
                                timeTo: overrideEntry?.timeTo ?? inheritedValues.timeTo,
                                hours: overrideEntry?.hours ?? inheritedValues.hours,
                              }
                              : inheritedValues;
                            const copyCodeGroup = getDayServiceCodeGroup(effectiveVariant);

                            const serviceDuration = getCopyUserServiceDuration(candidate.id, overrideEnabled);
                            const copyServiceDurationLabel = getServiceDurationLabelForVariant(
                              selectedEntryType,
                              effectiveVariant,
                              configuredEntryTypeMetaByValue,
                            );

                            return (
                              <Card key={candidate.id} variant="outlined" sx={{ p: 1.25, borderRadius: 2 }}>
                                <Stack spacing={1}>
                                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }}>
                                    <Stack direction="row" spacing={0.5} alignItems="center" sx={{ flex: 1, minWidth: 0 }}>
                                      <FormControlLabel
                                        sx={{ m: 0, minWidth: 0 }}
                                        control={(
                                          <Checkbox
                                            checked={selected}
                                            disabled={ownSelectionLocked}
                                            onChange={() => handleCopyUserToggle(candidate.id)}
                                            inputProps={{ 'aria-label': `${candidate.full_name} auswählen` }}
                                          />
                                        )}
                                        label={(
                                          <Typography variant="body2" sx={{ overflowWrap: 'anywhere' }}>
                                            {candidate.full_name}
                                          </Typography>
                                        )}
                                      />
                                      {isCurrentCandidate && <Chip size="small" label="Ich" color="primary" variant="outlined" />}
                                      {selected && !isCurrentCandidate && (
                                        <Tooltip title={isExpanded ? 'Individuelle Angaben einklappen' : 'Individuelle Angaben aufklappen'}>
                                          <IconButton
                                            size="small"
                                            onClick={() => handleCopyUserExpandedToggle(candidate.id)}
                                            aria-label={isExpanded ? `${candidate.full_name} einklappen` : `${candidate.full_name} aufklappen`}
                                          >
                                            {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                          </IconButton>
                                        </Tooltip>
                                      )}
                                    </Stack>

                                    {selected && !isCurrentCandidate && isExpanded && (
                                      shouldUseDayServiceCodeStep ? (
                                        <Button
                                          variant={overrideEnabled ? 'contained' : 'outlined'}
                                          color="secondary"
                                          onClick={() => handleCopyUserOverrideToggle(candidate.id)}
                                          sx={{ textTransform: 'none' }}
                                        >
                                          Zeiten/Kürzel anpassen
                                        </Button>
                                      ) : isDozentDayEntryType(selectedEntryType) ? (
                                        <Stack spacing={1} sx={{ flex: 1 }}>
                                          <Stack direction="row" spacing={1} sx={{ '& > *': { flex: 1, minWidth: 0 } }}>
                                            <Button
                                              variant={effectiveBaseVariant === ENTRY_VARIANT_TE1 ? 'contained' : 'outlined'}
                                              color="secondary"
                                              onClick={() => handleCopyUserVariantSelection(candidate.id, ENTRY_VARIANT_TE1)}
                                              sx={{ textTransform: 'none' }}
                                            >
                                              T (T1/T2 automatisch)
                                            </Button>
                                            <Button
                                              variant={effectiveBaseVariant === ENTRY_VARIANT_HONORAR ? 'contained' : 'outlined'}
                                              color="secondary"
                                              onClick={() => handleCopyUserVariantSelection(candidate.id, ENTRY_VARIANT_HONORAR)}
                                              sx={{ textTransform: 'none' }}
                                            >
                                              Honorar
                                            </Button>
                                          </Stack>
                                          <Stack direction="row" spacing={1} sx={{ '& > *': { flex: 1, minWidth: 0 } }}>
                                            <Button
                                              variant={isFlexEnabled ? 'contained' : 'outlined'}
                                              color="secondary"
                                              onClick={() => handleCopyUserTimeAdjustToggle(candidate.id)}
                                              sx={{ textTransform: 'none' }}
                                              disabled={!effectiveBaseVariant}
                                            >
                                              Flexibler Zeitraum
                                            </Button>
                                          </Stack>
                                        </Stack>
                                      ) : (
                                        <Button
                                          variant={overrideEnabled ? 'contained' : 'outlined'}
                                          color="secondary"
                                          onClick={() => handleCopyUserOverrideToggle(candidate.id)}
                                          sx={{ textTransform: 'none' }}
                                        >
                                          Zeiten anpassen
                                        </Button>
                                      )
                                    )}
                                  </Stack>

                                  {ownSelectionLocked && (
                                    <Typography variant="caption" color="text.secondary">
                                      Bei Sammelerfassungen als Einheitsführer muss das eigene Logbuch beteiligt bleiben.
                                    </Typography>
                                  )}

                                  {selected && !isCurrentCandidate && isExpanded && (
                                    <>
                                      {shouldShowCopyTimeInputs && (
                                        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.25}>
                                          <TextField
                                            fullWidth
                                            type="time"
                                            size="small"
                                            label="Zeitraum von"
                                            value={displayedValues.timeFrom || ''}
                                            onInput={(event) => handleCopyUserFieldChange(candidate.id, 'timeFrom', event.target.value)}
                                            onChange={(event) => handleCopyUserFieldChange(candidate.id, 'timeFrom', event.target.value)}
                                            inputProps={{ step: 60 }}
                                            InputLabelProps={{ shrink: true }}
                                          />
                                          <TextField
                                            fullWidth
                                            type="time"
                                            size="small"
                                            label="Zeitraum bis"
                                            value={displayedValues.timeTo || ''}
                                            onInput={(event) => handleCopyUserFieldChange(candidate.id, 'timeTo', event.target.value)}
                                            onChange={(event) => handleCopyUserFieldChange(candidate.id, 'timeTo', event.target.value)}
                                            inputProps={{ step: 60 }}
                                            InputLabelProps={{ shrink: true }}
                                          />
                                          {!shouldForceZeroHours && (
                                            <TextField
                                              fullWidth
                                              type="text"
                                              size="small"
                                              label="Höhenretterstunden (HH:MM)"
                                              value={displayedValues.hours === '' ? '' : displayedValues.hours}
                                              onChange={(event) => handleCopyUserFieldChange(candidate.id, 'hours', event.target.value)}
                                              placeholder="08:12"
                                            />
                                          )}
                                        </Stack>
                                      )}

                                      {shouldUseDayServiceCodeStep && overrideEnabled && (
                                        <Stack spacing={1}>
                                          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                                            {supportsA3Za3Selection && (
                                              <Button
                                                size="small"
                                                variant={copyCodeGroup === 'A' ? 'contained' : 'outlined'}
                                                onClick={() => handleCopyUserCodeGroupChange(candidate.id, 'A', serviceDuration)}
                                              >
                                                A3/zA3
                                              </Button>
                                            )}
                                            {supportsTGroupSelection && (
                                              <Button
                                                size="small"
                                                variant={copyCodeGroup === 'T' ? 'contained' : 'outlined'}
                                                onClick={() => handleCopyUserCodeGroupChange(candidate.id, 'T', serviceDuration)}
                                              >
                                                TE1/2
                                              </Button>
                                            )}
                                            {supportsHonorarSelection && (
                                              <Button
                                                size="small"
                                                variant={copyCodeGroup === 'HONORAR' ? 'contained' : 'outlined'}
                                                onClick={() => handleCopyUserCodeGroupChange(candidate.id, 'HONORAR', serviceDuration)}
                                              >
                                                Honorar
                                              </Button>
                                            )}
                                          </Stack>

                                          {copyCodeGroup && serviceDuration !== null && (
                                            <Typography variant="caption" color="text.secondary">
                                              Automatische Zuordnung: {copyCodeGroup === 'T'
                                                ? (serviceDuration <= 6 ? 'TE2' : 'TE1')
                                                : (copyCodeGroup === 'A'
                                                  ? (isExactA3ServiceDuration(serviceDuration) ? 'A3' : 'zA3')
                                                  : 'Honorar')}.
                                            </Typography>
                                          )}
                                        </Stack>
                                      )}

                                      {serviceDuration !== null ? (
                                        <Typography variant="caption" color="text.secondary">
                                          {copyServiceDurationLabel}: {(decimalHoursToDurationString(serviceDuration) || ZERO_DURATION)}h
                                        </Typography>
                                      ) : (
                                        <Typography variant="caption" color="warning.main">
                                          Dienstzeitraum kann nicht berechnet werden.
                                        </Typography>
                                      )}
                                    </>
                                  )}
                                </Stack>
                              </Card>
                            );
                          })}
                        </Stack>
                      </Stack>
                    </Card>
                  )}
                </>
              )}

              {shouldShowRow8 && (
                <>
                  <Typography variant="body2" color="text.secondary">
                    {incidentQuestionNumber}. Gab es besondere Vorkommnisse?
                  </Typography>

                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25}>
                    <Button
                      variant={incidentAnswer === 'ja' ? 'contained' : 'outlined'}
                      onClick={() => handleIncidentAnswerChange('ja')}
                      sx={{ minHeight: 44 }}
                    >
                      Ja
                    </Button>
                    <Button
                      variant={incidentAnswer === 'nein' ? 'contained' : 'outlined'}
                      onClick={() => handleIncidentAnswerChange('nein')}
                      sx={{ minHeight: 44 }}
                    >
                      Nein
                    </Button>
                  </Stack>

                  {incidentAnswer === 'ja' && (
                    <Card variant="outlined" sx={{ borderRadius: 2, p: 1.5 }}>
                      <Stack spacing={1.25}>
                        {incidentOptionsError && (
                          <Alert severity="warning">{incidentOptionsError}</Alert>
                        )}

                        {!incidentCanCreate && !incidentOptionsLoading && (
                          <Alert severity="warning">
                            Für Zwischenfälle besteht aktuell keine Erfassungsberechtigung.
                          </Alert>
                        )}

                        <IncidentFeedbackFormFields
                          formState={incidentFormState}
                          onFieldChange={handleIncidentFieldChange}
                          options={incidentOptions}
                          attachmentFiles={incidentAttachmentFiles}
                          onAttachmentFilesChange={setIncidentAttachmentFiles}
                          showAssetPickers
                        />
                      </Stack>
                    </Card>
                  )}
                </>
              )}

              {shouldShowRow9 && (
                <>
                  <Typography variant="body2" color="text.secondary">
                    {pdfQuestionNumber}. Soll ein Stundenprotokoll im PDF Format erstellt werden?
                  </Typography>

                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25}>
                    <Button
                      variant={answers.pdfProtocolAnswer === 'ja' ? 'contained' : 'outlined'}
                      onClick={() => handlePdfProtocolAnswerChange('ja')}
                      sx={{ minHeight: 44 }}
                    >
                      Ja
                    </Button>
                    <Button
                      variant={answers.pdfProtocolAnswer === 'nein' ? 'contained' : 'outlined'}
                      onClick={() => handlePdfProtocolAnswerChange('nein')}
                      sx={{ minHeight: 44 }}
                    >
                      Nein
                    </Button>
                  </Stack>
                </>
              )}

              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25}>
                <Button
                  variant="contained"
                  onClick={handleOpenConfirmSave}
                  disabled={!canOpenForm || saveLoading}
                  color={canOpenForm ? 'success' : 'inherit'}
                  sx={{ minHeight: 44 }}
                >
                  {saveLoading ? 'Speichert...' : 'Speichern'}
                </Button>
                <Button
                  variant="contained"
                  color="error"
                  disabled={saveLoading}
                  onClick={handleResetWizard}
                  sx={{ minHeight: 44 }}
                >
                  Eingabe abbrechen
                </Button>
              </Stack>
            </Stack>
          </CardContent>
        </Card>

        <Dialog open={confirmSaveOpen} onClose={() => !saveLoading && setConfirmSaveOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Eintragung prüfen</DialogTitle>
          <DialogContent dividers>
            <Stack spacing={1}>
              <Typography variant="body2"><strong>Datum:</strong> {selectedDate || '-'}</Typography>
              <Typography variant="body2"><strong>Tätigkeit:</strong> {getEffectiveEntryTypeForVariant(selectedEntryType, answers.entryVariant) || '-'}</Typography>
              <Typography variant="body2"><strong>Verantwortliche Person:</strong> {responsibleName}</Typography>
              {!isSrhtBasicCourseWorkflow && (
                <Typography variant="body2"><strong>Zeitraum:</strong> {answers.timeFrom || '-'} bis {answers.timeTo || '-'}</Typography>
              )}
              {!isSrhtBasicCourseWorkflow && (
                <Typography variant="body2"><strong>{serviceDurationLabel}:</strong> {adjustedServiceDuration !== null ? `${decimalHoursToDurationString(adjustedServiceDuration) || ZERO_DURATION}h` : '-'}</Typography>
              )}
              <Typography variant="body2"><strong>Höhenretterstunden:</strong> {isSrhtBasicCourseWorkflow ? '120 (ohne Anrechnung auf den Jahresfortschritt)' : (shouldForceZeroHours ? ZERO_DURATION : (answers.hours === '' ? '-' : answers.hours))}</Typography>
              {Array.isArray(answers.keywords) && answers.keywords.length > 0 && (
                <Typography variant="body2"><strong>Schlagwörter:</strong> {answers.keywords.join(', ')}</Typography>
              )}
              {isDayServiceWizardFlow && row4ServiceMode === 'individual' && (
                <Typography variant="body2"><strong>Pausen:</strong> {answers.pauseAnswer === 'ja' ? 'Ja' : 'Nein'}</Typography>
              )}
              {shouldShowRow7 && (
                <Typography variant="body2">
                  <strong>Betroffene Logbücher:</strong>{' '}
                  {answers.copyToOthers === 'ja'
                    ? copyUsersSummary.map((entry) => (
                      entry.hasOverride
                        ? `${entry.label} (${entry.variantLabel ? `${entry.variantLabel}, ` : ''}${entry.timeLabel})`
                        : entry.label
                    )).join(', ')
                    : (user?.full_name || user?.fullName || 'Eigenes Logbuch')}
                </Typography>
              )}
              {shouldShowRow8 && (
                <Typography variant="body2"><strong>Besondere Vorkommnisse:</strong> {incidentAnswer === 'ja' ? 'Ja' : 'Nein'}</Typography>
              )}
              {shouldShowRow9 && (
                <Typography variant="body2"><strong>PDF-Protokoll:</strong> {answers.pdfProtocolAnswer === 'ja' ? 'Ja' : 'Nein'}</Typography>
              )}

              {saveError && <Alert severity="error">{saveError}</Alert>}

              <Typography variant="body2" sx={{ mt: 1, fontWeight: 700 }}>
                Möchten Sie die Eintragung so in das Logbuch / die Logbücher übernehmen?
              </Typography>
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setConfirmSaveOpen(false)} disabled={saveLoading}>Zurück</Button>
            <Button onClick={handleConfirmSave} variant="contained" color="success" disabled={saveLoading}>
              {saveLoading ? 'Speichert...' : 'Jetzt speichern'}
            </Button>
          </DialogActions>
        </Dialog>
      </Stack>
    </Box>
  );
}

export default EintragVornehmenPage;
